import { Component, StrictMode, useEffect, useMemo, useRef, useState, type ErrorInfo, type FormEvent, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { AGENT_DEFINITIONS, type AgentDefinition } from "@saydian/v2-contracts";
import { ContentWorkflowPanel, GoogleShoppingPanel, GrowthCenter, KlaviyoMarketConnection, KnowledgeSearchPanel, PostizConnections, ReviewCenterPanel, SocialConnections, TaskOperations } from "./complete-centers";
import "./styles.css";
import { WORKSPACE } from "./workspace-client";

type User = {
  id: string;
  username: string;
  displayName: string;
  permissions: string[];
  csrfToken: string;
};
type AgentState = AgentDefinition & {
  state?: {
    enabled: boolean;
    queued: string;
    running: string;
    review: string;
    failed: string;
  };
};
type Json = Record<string, any>;
type FocusTarget = { type: string; id: string; mode: "preview" | "edit" };

const menus = [
  ["overview", "增长总览"],
  ["products", "产品中心"],
  ["assets", "素材中心"],
  ["knowledge", "知识中心"],
  ["content", "内容中心"],
  ["storefront", "网站装修管理"],
  ["agents", "Agent 中心"],
  ["reviews", "审核中心"],
  ["experiments", "增长实验"],
  ["data", "数据中心"],
  ["connections", "连接与系统"],
] as const;

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v2${path}`, {
    credentials: "include",
    ...options,
    headers: { Accept: "application/json", ...(options.headers || {}) },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.message || payload.error || `请求失败：${response.status}`);
  return payload as T;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [view, setView] = useState("overview");
  const [focusContentId, setFocusContentId] = useState<string | null>(null);
  const [focusAgentKey, setFocusAgentKey] = useState<string | null>(null);
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const [agents, setAgents] = useState<AgentState[]>([...AGENT_DEFINITIONS]);
  const [health, setHealth] = useState<{
    status?: string;
    database?: { ok?: boolean };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.allSettled([api<{ user: User }>("/auth/me").then((value) => setUser(value.user)), api<{ status: string; database: { ok: boolean } }>("/health").then(setHealth)]).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (user)
      api<{ agents: AgentState[] }>("/agents")
        .then((value) => setAgents(value.agents))
        .catch(() => {});
  }, [user]);
  if (loading) return <div className="loading-screen">正在加载 SAYDIAN {WORKSPACE.marketLabel} Growth OS V2.0…</div>;
  if (!user) return <Login onLogin={setUser} health={health} />;
  const activeLabel = menus.find(([key]) => key === view)?.[1] || "增长总览";
  const navigate = (nextView: string, agentKey: string | null = null) => {
    setFocusContentId(null);
    setFocusTarget(null);
    setFocusAgentKey(agentKey);
    setView(nextView);
  };
  const openRecord = (type: string, id: string, mode: "preview" | "edit" = "preview") => {
    const nextView = type === "content" ? "content" : type === "ai_task" || type === "recommendation" ? "agents" : type === "product" || type === "market_product" ? "products" : type === "asset" ? "assets" : "knowledge";
    setFocusContentId(type === "content" ? id : null);
    setFocusAgentKey(null);
    setFocusTarget({ type, id, mode });
    setView(nextView);
  };
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">S</span>
          <div>
            <strong>SAYDIAN</strong>
            <small>AI Growth OS · V2.0 · {WORKSPACE.code}</small>
          </div>
        </div>
        <nav>
          {menus.map(([key, label], index) => (
            <button key={key} className={view === key ? "active" : ""} onClick={() => navigate(key)}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              {label}
            </button>
          ))}
        </nav>
        <div className="sidebar-user">
          <span>{user.displayName}</span>
          <small>{WORKSPACE.customerLabel}</small>
          <button onClick={() => logout(user, setUser)}>退出登录</button>
        </div>
      </aside>
      <main className="workspace">
        <header>
          <div>
            <p>{WORKSPACE.localized ? `${WORKSPACE.marketLabel}市场 · 中文管理 / ${WORKSPACE.code === "FR" ? "法语" : "德语"}产出` : "海外智能健康增长控制中心"}</p>
            <h1>{activeLabel}</h1>
          </div>
          <div className={`system-chip ${health?.database?.ok ? "ok" : "warning"}`}>
            <i />
            {health?.database?.ok ? "V2 数据库正常" : "V2 数据库待配置"}
          </div>
        </header>
        <ViewErrorBoundary viewKey={`${view}:${focusTarget?.type || ""}:${focusTarget?.id || ""}`}>
          <View view={view} agents={agents} user={user} focusContentId={focusContentId} focusAgentKey={focusAgentKey} focusTarget={focusTarget} onNavigate={navigate} onOpenRecord={openRecord} onOpenAgent={(agentKey) => navigate("agents", agentKey)} />
        </ViewErrorBoundary>
      </main>
    </div>
  );
}

class ViewErrorBoundary extends Component<{ viewKey: string; children: ReactNode }, { error: Error | null }> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidUpdate(previous: { viewKey: string }) {
    if (previous.viewKey !== this.props.viewKey && this.state.error) this.setState({ error: null });
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("SAYDIAN 管理界面局部渲染失败", error, info.componentStack);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <section className="page">
        <div className="empty error-boundary">
          <h2>当前操作窗口未能正常呈现</h2>
          <p>系统数据没有被修改。请关闭该窗口后重试；若问题持续，请将此错误提交给技术人员。</p>
          <code>{this.state.error.message || "未知界面错误"}</code>
          <div className="actions">
            <button className="primary" onClick={() => this.setState({ error: null })}>
              重新加载当前界面
            </button>
          </div>
        </div>
      </section>
    );
  }
}

function Login({ onLogin, health }: { onLogin: (user: User) => void; health: { database?: { ok?: boolean } } | null }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      const result = await api<{ user: User }>("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: data.get("username"),
          password: data.get("password"),
        }),
      });
      onLogin(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录失败");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="login">
      <section>
        <div className="brand light">
          <span className="brand-mark">S</span>
          <strong>SAYDIAN</strong>
        </div>
        <p className="kicker">AI GROWTH OS · V2.0</p>
        <h1>
          让产品、内容、Agent与数据，
          <br />
          围绕增长目标共同工作。
        </h1>
        <p className="login-copy">七个Agent使用统一产品事实、证据和人工审核边界。本地Codex负责执行，外部系统保持最小权限。</p>
        <div className="flow">
          <span>机会</span>
          <span>生成</span>
          <span>审核</span>
          <span>分发</span>
          <span>复盘</span>
        </div>
      </section>
      <form onSubmit={submit}>
        <span className="version-pill">正式版</span>
        <h2>登录V2控制台</h2>
        <label>
          管理员账号
          <input name="username" autoComplete="username" required />
        </label>
        <label>
          密码
          <input name="password" type="password" autoComplete="current-password" required />
        </label>
        <button disabled={busy}>{busy ? "正在验证…" : "进入平台"}</button>
        {error && <p className="error">{error}</p>}
        <small className="security">● PostgreSQL：{health?.database?.ok ? "已连接" : "待迁移或未配置"}</small>
      </form>
    </main>
  );
}

function View({ view, agents, user, focusContentId, focusAgentKey, focusTarget, onOpenRecord, onNavigate, onOpenAgent }: { view: string; agents: AgentState[]; user: User; focusContentId: string | null; focusAgentKey: string | null; focusTarget: FocusTarget | null; onOpenRecord: (type: string, id: string, mode?: "preview" | "edit") => void; onNavigate: (view: string) => void; onOpenAgent: (agentKey: string) => void }) {
  if (view === "overview") return <Overview agents={agents} user={user} onOpenRecord={onOpenRecord} onNavigate={onNavigate} onOpenAgent={onOpenAgent} />;
  if (view === "agents") return <AgentCenter agents={agents} user={user} focusAgentKey={focusAgentKey} focusTaskId={focusTarget?.type === "ai_task" ? focusTarget.id : null} />;
  if (view === "products") return <Products user={user} focusTarget={focusTarget?.type === "product" || focusTarget?.type === "market_product" ? focusTarget : null} />;
  if (view === "assets") return <Assets user={user} focusTarget={focusTarget?.type === "asset" ? focusTarget : null} />;
  if (view === "knowledge") return <Knowledge user={user} focusTarget={focusTarget?.type === "knowledge" ? focusTarget : null} />;
  if (view === "content") return <Content user={user} focusContentId={focusContentId} focusMode={focusTarget?.type === "content" ? focusTarget.mode : "preview"} />;
  if (view === "storefront") return <StorefrontDesign user={user} />;
  if (view === "reviews") return <Reviews user={user} onOpenRecord={onOpenRecord} />;
  if (view === "data") return <Metrics user={user} />;
  if (view === "connections") return <Integrations user={user} />;
  return <GrowthCenter user={user} />;
}

function StorefrontDesign({ user }: { user: User }) {
  const [design, setDesign] = useState<Json | null>(null);
  const [document, setDocument] = useState<Json | null>(null);
  const [selected, setSelected] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const canApprove = user.permissions.includes("content.approve");

  const applyDesign = (next: Json | null) => {
    setDesign(next);
    const nextDocument = next?.draft_document || null;
    setDocument(nextDocument);
    const order = Array.isArray(nextDocument?.order) ? nextDocument.order : [];
    const sectionKeys = Object.keys(nextDocument?.sections || {});
    setSelected((current) => current && sectionKeys.includes(current) ? current : String(order[0] || sectionKeys[0] || ""));
  };
  const load = () => api<{ design: Json | null }>("/storefront-design").then((result) => applyDesign(result.design));
  useEffect(() => { load().catch((error: Error) => setMessage(error.message)); }, []);

  async function mutate(path: string, options: RequestInit, fallback: string): Promise<string | null> {
    setBusy(true);
    setMessage("");
    try {
      const result = await api<{ design: Json; message?: string }>(path, options);
      applyDesign(result.design);
      setMessage(result.message || fallback);
      return null;
    } catch (error) {
      const detail = error instanceof Error ? error.message : fallback;
      setMessage(detail);
      return detail;
    } finally {
      setBusy(false);
    }
  }

  async function syncTheme(force = false) {
    const error = await mutate("/storefront-design/sync", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": user.csrfToken },
      body: JSON.stringify({ force }),
    }, "已同步线上主题");
    if (!force && error?.includes("未发布的装修草稿") && window.confirm("同步会放弃当前未发布草稿，确定继续吗？")) await syncTheme(true);
  }

  const sections = document?.sections || {};
  const orderedIds = Array.from(new Set([...(Array.isArray(document?.order) ? document.order : []), ...Object.keys(sections)]));
  const selectedSection = selected ? sections[selected] as Json | undefined : undefined;
  const editable = Boolean(design && ['draft', 'rejected'].includes(design.status));

  function updateSetting(sectionId: string, key: string, value: unknown) {
    setDocument((current) => {
      if (!current) return current;
      const section = current.sections?.[sectionId] || {};
      return { ...current, sections: { ...current.sections, [sectionId]: { ...section, settings: { ...(section.settings || {}), [key]: value } } } };
    });
  }

  function updateBlockSetting(sectionId: string, blockId: string, key: string, value: unknown) {
    setDocument((current) => {
      if (!current) return current;
      const section = current.sections?.[sectionId] || {};
      const blocks = section.blocks || {};
      const block = blocks[blockId] || {};
      return { ...current, sections: { ...current.sections, [sectionId]: { ...section, blocks: { ...blocks, [blockId]: { ...block, settings: { ...(block.settings || {}), [key]: value } } } } } };
    });
  }

  function moveSection(direction: -1 | 1) {
    setDocument((current) => {
      if (!current || !selected) return current;
      const order = [...(Array.isArray(current.order) ? current.order : [])];
      const index = order.indexOf(selected);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= order.length) return current;
      [order[index], order[target]] = [order[target], order[index]];
      return { ...current, order };
    });
  }

  function toggleSection() {
    setDocument((current) => {
      if (!current || !selected) return current;
      const section = current.sections?.[selected] || {};
      return { ...current, sections: { ...current.sections, [selected]: { ...section, disabled: !section.disabled } } };
    });
  }

  return (
    <section className="page storefront-design-page">
      <ModuleHeading
        kicker="Shopify Online Store"
        title="网站装修管理"
        copy="同步当前在线主题，编辑首页 Banner、轮播、文案与产品展示区；所有改动必须经过人工审核后才能发布。"
        badge={design ? designStatusLabel(design.status) : "尚未同步"}
      />
      {message && <p className="message">{message}</p>}
      <div className="asset-toolbar storefront-toolbar">
        <div>
          <strong>{design?.theme_name || "尚未读取 Shopify 主题"}</strong>
          <p className="field-note">{design ? `方案版本 ${design.version} · 最近同步 ${formatDateTime(design.last_synced_at)}` : "首次使用请先同步线上主题，不会修改外显网站。"}</p>
        </div>
        <div className="actions">
          {design?.storefront_url && <a className="secondary" href={design.storefront_url} target="_blank" rel="noreferrer">预览当前网站</a>}
          <button className="secondary" disabled={busy} onClick={() => syncTheme(false)}>{busy ? "处理中…" : "同步线上主题"}</button>
        </div>
      </div>

      {!design || !document ? (
        <div className="empty-state storefront-empty">
          <h3>从 Shopify 在线主题开始</h3>
          <p>系统只读取首页结构并生成内部草稿，不会立即影响访客看到的网站。</p>
          <button className="primary" disabled={busy} onClick={() => syncTheme(false)}>同步并创建装修草稿</button>
        </div>
      ) : (
        <>
          <div className="storefront-workflow">
            {["draft", "in_review", "approved", "published"].map((status, index) => (
              <span key={status} className={designWorkflowReached(design.status, status) ? "active" : ""}>{index + 1}. {designStatusLabel(status)}</span>
            ))}
          </div>
          <div className="storefront-layout">
            <aside className="storefront-section-list">
              <div className="card-row"><h3>首页区块</h3><span className="count">{orderedIds.length}</span></div>
              {orderedIds.map((id, index) => {
                const section = sections[id] || {};
                return <button key={id} className={selected === id ? "active" : ""} onClick={() => setSelected(id)}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <div><strong>{themeSectionTitle(section)}</strong><small>{themeSectionType(section)}</small></div>
                </button>;
              })}
            </aside>
            <div className="storefront-editor panel">
              {selectedSection ? (
                <>
                  <div className="card-row">
                    <div><span className="eyebrow">首页区块</span><h3>{themeSectionTitle(selectedSection)}</h3></div>
                    <div className="section-controls">
                      {selectedSection.disabled && <span className="status muted">当前隐藏</span>}
                      {editable && <><button className="text-button" onClick={() => moveSection(-1)}>上移</button><button className="text-button" onClick={() => moveSection(1)}>下移</button><button className="text-button" onClick={toggleSection}>{selectedSection.disabled ? "显示" : "隐藏"}</button></>}
                    </div>
                  </div>
                  <ThemeSettingsEditor settings={selectedSection.settings || {}} disabled={!editable} onChange={(key, value) => updateSetting(selected, key, value)} />
                  {Object.entries(selectedSection.blocks || {}).map(([blockId, block]) => {
                    const item = block as Json;
                    return <div className="theme-block" key={blockId}>
                      <h4>{themeSectionTitle(item)}</h4>
                      <ThemeSettingsEditor settings={item.settings || {}} disabled={!editable} onChange={(key, value) => updateBlockSetting(selected, blockId, key, value)} />
                    </div>;
                  })}
                </>
              ) : <div className="empty-state"><p>该主题暂未返回可编辑首页区块。</p></div>}
            </div>
          </div>
          <div className="storefront-publish-bar">
            <div><strong>{designStatusLabel(design.status)}</strong><p className="field-note">发布前会再次核对线上主题版本，防止覆盖 Shopify 后台里的新改动。</p></div>
            <div className="actions">
              {['draft', 'rejected'].includes(design.status) && <button className="secondary" disabled={busy} onClick={() => mutate("/storefront-design", { method: "PATCH", headers: { "Content-Type": "application/json", "X-CSRF-Token": user.csrfToken }, body: JSON.stringify({ title: design.title, document }) }, "草稿已保存")}>保存草稿</button>}
              {design.status === 'draft' && <button className="primary" disabled={busy} onClick={() => mutate("/storefront-design/submit-review", { method: "POST", headers: { "X-CSRF-Token": user.csrfToken } }, "已提交审核")}>提交审核</button>}
              {design.status === 'in_review' && canApprove && <><button className="secondary" disabled={busy} onClick={() => mutate("/storefront-design/decision", { method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": user.csrfToken }, body: JSON.stringify({ decision: "reject" }) }, "已驳回")}>驳回修改</button><button className="primary" disabled={busy} onClick={() => mutate("/storefront-design/decision", { method: "POST", headers: { "Content-Type": "application/json", "X-CSRF-Token": user.csrfToken }, body: JSON.stringify({ decision: "approve" }) }, "已批准")}>批准方案</button></>}
              {design.status === 'approved' && canApprove && <button className="primary danger-safe" disabled={busy} onClick={() => window.confirm("确认将已审核方案发布到 Shopify 外显网站？") && mutate("/storefront-design/publish", { method: "POST", headers: { "X-CSRF-Token": user.csrfToken } }, "已发布")}>发布到外显网站</button>}
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function ThemeSettingsEditor({ settings, disabled, onChange }: { settings: Json; disabled: boolean; onChange: (key: string, value: unknown) => void }) {
  const entries = Object.entries(settings).filter(([, value]) => ["string", "number", "boolean"].includes(typeof value));
  if (!entries.length) return <p className="field-note">这个区块没有可直接编辑的文字或开关。</p>;
  return <div className="theme-settings-grid">{entries.map(([key, value]) => {
    const label = themeSettingLabel(key);
    if (typeof value === "boolean") return <label className="theme-toggle" key={key}><input type="checkbox" checked={value} disabled={disabled} onChange={(event) => onChange(key, event.target.checked)} /><span>{label}</span></label>;
    if (typeof value === "number") return <label key={key}>{label}<input type="number" value={value} disabled={disabled} onChange={(event) => onChange(key, Number(event.target.value))} /></label>;
    const multiline = value.length > 80 || /text|description|subheading|content/i.test(key);
    return <label className={multiline ? "wide" : ""} key={key}>{label}{multiline ? <textarea value={value} disabled={disabled} rows={3} onChange={(event) => onChange(key, event.target.value)} /> : <input value={value} disabled={disabled} onChange={(event) => onChange(key, event.target.value)} />}</label>;
  })}</div>;
}

function themeSectionTitle(section: Json): string {
  const settings = section?.settings || {};
  return String(settings.heading || settings.title || settings.text || themeSectionType(section) || "未命名区块").replace(/<[^>]+>/g, "").slice(0, 50);
}
function themeSectionType(section: Json): string { return String(section?.type || "自定义区块").replace(/[-_]/g, " "); }
function themeSettingLabel(key: string): string {
  const labels: Record<string, string> = { heading: "主标题", title: "标题", text: "正文", subheading: "副标题", button_label: "按钮文字", button_link: "按钮链接", link: "链接", image: "图片", image_2: "第二张图片", product: "产品", collection: "产品集合", color_scheme: "配色方案", show_text_box: "显示文字框", autoplay: "自动轮播", slide_height: "轮播高度", desktop_image_width: "桌面图片宽度" };
  return labels[key] || key.replace(/[-_]/g, " ");
}
function designStatusLabel(status: string): string {
  return ({ draft: "草稿", in_review: "待审核", approved: "已批准", rejected: "已驳回", published: "已发布" } as Record<string, string>)[status] || status;
}
function designWorkflowReached(current: string, target: string): boolean {
  const level: Record<string, number> = { rejected: 0, draft: 0, in_review: 1, approved: 2, published: 3 };
  return (level[current] ?? 0) >= (level[target] ?? 0);
}
function formatDateTime(value: unknown): string {
  const date = new Date(String(value || ""));
  return Number.isNaN(date.getTime()) ? "未知" : date.toLocaleString("zh-CN", { hour12: false });
}

function GrowthExperiments({ user }: { user: User }) {
  const [data, setData] = useState<{
    experiments: Json[];
    opportunities: Json[];
    competitors: Json[];
    recommendations: Json[];
  }>({
    experiments: [],
    opportunities: [],
    competitors: [],
    recommendations: [],
  });
  const [message, setMessage] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Json>({
    title: "",
    hypothesis: "",
    channel: "content",
    primaryMetric: "clicks",
    baselineValue: "",
    targetValue: "",
    status: "planning",
  });
  const load = () => api<typeof data>("/growth").then(setData);
  useEffect(() => {
    load().catch((error: Error) => setMessage(error.message));
  }, []);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const result = await api<{ message?: string }>("/growth/experiments", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(form),
      });
      setMessage(result.message || "增长实验已创建");
      setCreating(false);
      setForm({
        title: "",
        hypothesis: "",
        channel: "content",
        primaryMetric: "clicks",
        baselineValue: "",
        targetValue: "",
        status: "planning",
      });
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "创建实验失败");
    }
  }
  async function transition(item: Json, status: string) {
    try {
      const result = await api<{ message?: string }>(
        `/growth/experiments/${item.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": user.csrfToken,
          },
          body: JSON.stringify({ status }),
        },
      );
      setMessage(result.message || "实验状态已更新");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "更新实验失败");
    }
  }
  return (
    <section className="page">
      <ModuleHeading
        kicker="机会 → 活动 → 实验 → 复盘"
        title="增长实验"
        copy="只记录经过人工批准的实验假设与真实指标；本模块不会直接修改广告预算或发布内容。"
        badge={`${data.experiments.length} 个实验`}
      />
      {message && <p className="message">{message}</p>}
      <div className="asset-toolbar">
        <p className="field-note">
          新机会 {data.opportunities.length} · 已监控竞品{" "}
          {data.competitors.length} · 待处理建议 {data.recommendations.length}
        </p>
        <button className="primary" onClick={() => setCreating(true)}>
          新建实验
        </button>
      </div>
      {creating && (
        <div className="preview-overlay" role="dialog" aria-modal="true">
          <form className="preview-dialog editor-window" onSubmit={create}>
            <div className="card-row">
              <h3>新建受控增长实验</h3>
              <button
                type="button"
                className="text-button"
                onClick={() => setCreating(false)}
              >
                关闭
              </button>
            </div>
            <div className="form-grid compact-form">
              <label className="wide">
                实验名称
                <input
                  required
                  value={form.title}
                  onChange={(event) =>
                    setForm({ ...form, title: event.target.value })
                  }
                />
              </label>
              <label className="wide">
                实验假设
                <textarea
                  required
                  value={form.hypothesis}
                  onChange={(event) =>
                    setForm({ ...form, hypothesis: event.target.value })
                  }
                  placeholder="例如：以睡眠趋势为主题的页面可提高自然搜索点击率"
                />
              </label>
              <label>
                渠道
                <input
                  required
                  value={form.channel}
                  onChange={(event) =>
                    setForm({ ...form, channel: event.target.value })
                  }
                />
              </label>
              <label>
                主指标
                <input
                  required
                  value={form.primaryMetric}
                  onChange={(event) =>
                    setForm({ ...form, primaryMetric: event.target.value })
                  }
                />
              </label>
              <label>
                基线值
                <input
                  inputMode="decimal"
                  value={form.baselineValue}
                  onChange={(event) =>
                    setForm({ ...form, baselineValue: event.target.value })
                  }
                />
              </label>
              <label>
                目标值
                <input
                  inputMode="decimal"
                  value={form.targetValue}
                  onChange={(event) =>
                    setForm({ ...form, targetValue: event.target.value })
                  }
                />
              </label>
            </div>
            <div className="actions">
              <button className="primary">创建计划</button>
              <button
                type="button"
                className="secondary"
                onClick={() => setCreating(false)}
              >
                取消
              </button>
            </div>
          </form>
        </div>
      )}
      <div className="list-grid">
        {data.experiments.map((item) => (
          <article className="record-card" key={item.id}>
            <div className="card-row">
              <span className="eyebrow">
                {item.channel} · {item.primary_metric}
              </span>
              <Status value={item.status} />
            </div>
            <h3>{item.title}</h3>
            <p>{item.hypothesis}</p>
            <small>
              基线：{item.baseline_value ?? "—"} · 目标：
              {item.target_value ?? "—"}
            </small>
            <div className="actions">
              {item.status === "planning" && (
                <button
                  className="secondary"
                  onClick={() => transition(item, "running")}
                >
                  标记开始
                </button>
              )}
              {item.status === "running" && (
                <button
                  className="secondary"
                  onClick={() => transition(item, "completed")}
                >
                  进入复盘
                </button>
              )}
              {item.status !== "archived" && (
                <button
                  className="text-button"
                  onClick={() => transition(item, "archived")}
                >
                  归档
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {!data.experiments.length && (
        <div className="empty">
          暂无增长实验。请从已验证机会开始，先建立假设与指标，再人工启动外部渠道。
        </div>
      )}
      <h2 className="section-title">优先市场机会</h2>
      <RecordTable
        rows={data.opportunities.slice(0, 10)}
        columns={[
          "keyword",
          "intent",
          "opportunity_score",
          "recommended_action",
          "status",
        ]}
        empty="暂无机会数据"
      />
    </section>
  );
}

function Overview({ agents, user, onOpenRecord, onNavigate, onOpenAgent }: { agents: AgentState[]; user: User; onOpenRecord: (type: string, id: string, mode?: "preview" | "edit") => void; onNavigate: (view: string) => void; onOpenAgent: (agentKey: string) => void }) {
  const [summary, setSummary] = useState<Json | null>(null);
  const totals = useMemo(() => agents.reduce((sum, agent) => sum + (agent.dailyTarget || 0), 0), [agents]);
  const [reviewCount, setReviewCount] = useState(0);
  useEffect(() => {
    api<{ overview: Json }>("/catalog/overview")
      .then((value) => setSummary(value.overview))
      .catch(() => {});
  }, []);
  const total = (key: string, field = "total") => summary?.[key]?.[field] ?? "—";
  const openReview = (type: string, id: string, mode: "preview" | "edit" = "preview") => onOpenRecord(type, id, mode);
  return (
    <section className="page">
      <div className="hero">
        <div>
          <span>正式运行边界</span>
          <h2>多市场 · 七个 Agent 协同</h2>
          <p>
            以 Shopify 产品事实、可追溯知识和人工审核为边界；外部动作只在授权与审核均通过后执行。
            {WORKSPACE.code === "DE" ? "德国首发：W9S。" : "产品范围：E8 / R7 / W9S。"}
            {WORKSPACE.code === "FR" ? " 法国结账在全部市场与三款产品门槛验证前保持关闭。" : ""}
          </p>
        </div>
        <div className="hero-number">
          <strong>{totals}</strong>
          <small>每日计划产出单元</small>
        </div>
      </div>
      <div className="stat-grid">
        <article className="stat-card" role="button" tabIndex={0} onClick={() => onNavigate("products")}>
          <small>产品事实</small>
          <strong>{total("products")}</strong>
          <span>已导入产品</span>
        </article>
        <article className="stat-card" role="button" tabIndex={0} onClick={() => onNavigate("content")}>
          <small>内容版本</small>
          <strong>{total("content")}</strong>
          <span>已发布 {total("content", "published")}</span>
        </article>
        <article className="stat-card" role="button" tabIndex={0} onClick={() => onNavigate("reviews")}>
          <small>待我审核</small>
          <strong>{reviewCount}</strong>
          <span>内容、产品、素材、知识与任务</span>
        </article>
        <article className="stat-card" role="button" tabIndex={0} onClick={() => onNavigate("assets")}>
          <small>待校验素材</small>
          <strong>{total("stagedAssets")}</strong>
          <span>不可直接用于发布</span>
        </article>
      </div>
      <ReviewCenterPanel user={user} compact onOpen={openReview} onCount={setReviewCount} />
      <h2 className="section-title">Agent运行概览</h2>
      <div className="agent-grid">
        {agents.map((agent) => (
          <AgentCard key={agent.key} agent={agent} onOpen={() => onOpenAgent(agent.key)} />
        ))}
      </div>
    </section>
  );
}

function productEditorValue(item: Json): Json {
  const primary = item.variants?.[0] || {};
  return {
    ...item,
    productTitle: item.product_title || primary.title || item.name || "",
    sku: primary.sku || "",
    price: primary.price || "",
    inventory: primary.inventory_quantity ?? "",
    variants: (item.variants || []).map((variant: Json) => ({
      ...variant,
      inventory: variant.inventory_quantity ?? variant.inventory ?? "",
      optionValuesText: Object.entries(variant.optionValues || variant.option_values_json || {})
        .map(([key, value]) => `${key}=${value}`)
        .join(", "),
    })),
  };
}

function Products({ user, focusTarget }: { user: User; focusTarget?: FocusTarget | null }) {
  const [items, setItems] = useState<Json[]>([]);
  const [message, setMessage] = useState("");
  const [editing, setEditing] = useState<Json | null>(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [localizing, setLocalizing] = useState<Json | null>(null);
  const [readiness, setReadiness] = useState<Json | null>(null);
  const load = async () => {
    setLoading(true);
    try {
      const [value, workspace] = await Promise.all([api<{ products: Json[] }>("/catalog/products"), api<Json>("/workspace/bootstrap")]);
      setItems(value.products);
      setReadiness(workspace.compliance || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load().catch((e: Error) => setMessage(e.message));
  }, []);
  useEffect(() => {
    if (!focusTarget || !items.length) return;
    const item = items.find((row) => String(row.id) === focusTarget.id);
    if (!item) {
      setMessage("未找到需要审核的产品记录，可能已被归档或删除。");
      return;
    }
    setCreating(false);
    if (focusTarget.type === "market_product") openLocalization(item);
    else setEditing(productEditorValue(item));
    document.getElementById(`product-${item.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusTarget, items]);

  async function save(input: Json) {
    try {
      const endpoint = input.id ? `/catalog/products/${input.id}` : "/catalog/products";
      const result = await api<{ message?: string }>(endpoint, {
        method: input.id ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(result.message || "产品资料已保存");
      setEditing(null);
      setCreating(false);
      await load();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "保存失败");
    }
  }

  function previewShopify(url: string) {
    const popup = window.open(url, "saydian_shopify_preview", "popup=yes,width=1120,height=820,resizable=yes,scrollbars=yes");
    if (popup) popup.focus();
    else {
      window.open(url, "_blank", "noopener,noreferrer");
      setMessage("浏览器拦截了预览小窗口，已改在新标签页打开 Shopify 商品。");
    }
  }

  async function openLocalization(item: Json) {
    try {
      const value = await api<{ localization: Json }>(`/catalog/products/${item.id}/market-localization`);
      setLocalizing(value.localization);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "无法读取市场产品资料");
    }
  }
  async function localizationAction(path: string, method: "PATCH" | "POST", body: Json = {}) {
    if (!localizing) return;
    try {
      const result = await api<{ localization: Json; message?: string }>(`/catalog/products/${localizing.product_id}/market-localization${path}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(body),
      });
      setLocalizing(result.localization);
      setMessage(result.message || "市场产品资料已更新");
      await load();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "市场产品操作失败");
    }
  }

  return (
    <section className="page">
      <ModuleHeading kicker="统一产品事实源" title="产品中心" copy="产品名称用于内部识别，产品标题用于 Shopify 展示；设为“已批准并发布”时，V2 会先校验 Shopify 授权再写入并发布 Online Store。" badge={loading ? "正在加载" : `${items.length} 个产品`} />
      {message && <p className="message">{message}</p>}
      {WORKSPACE.localized && readiness && <MarketReadinessPanel data={readiness} user={user} onReload={load} onMessage={setMessage} />}
      {!WORKSPACE.localized && user.permissions.includes("product.edit") && (
        <div className="actions page-actions">
          <button
            className="primary"
            onClick={() => {
              setEditing({
                name: "",
                productTitle: "",
                category: "Wearable",
                description: "",
                handle: "",
                status: "draft",
                variants: [
                  {
                    title: "默认款",
                    sku: "",
                    price: "",
                    inventory: "",
                    barcode: "",
                    optionValuesText: "",
                  },
                ],
              });
              setCreating(true);
            }}
          >
            新增产品
          </button>
        </div>
      )}
      {(creating || editing) && (
        <ProductEditor
          key={editing?.id || "new-product"}
          item={editing!}
          creating={creating}
          canApprove={user.permissions.includes("product.approve")}
          onCancel={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={save}
        />
      )}
      {loading && <div className="empty loading-state">正在读取产品事实与 Shopify 同步状态…</div>}
      {!loading && (
        <div className="list-grid">
          {items.map((item) => {
            const product = productEditorValue(item);
            return (
              <article className="record-card" id={`product-${item.id}`} key={item.id}>
                <div className="card-row">
                  <span className="eyebrow">
                    SKU · {product.sku || item.product_code || "未填写"} · {item.category}
                  </span>
                  <Status value={item.status} />
                </div>
                <h3>{item.name || item.product_title}</h3>
                {item.product_title && item.product_title !== item.name && <small>产品标题：{item.product_title}</small>}
                <p>{item.description || "暂无产品描述"}</p>
                <small>
                  型号：{item.model_code || "未建档"} · 证据：
                  {item.evidence_status || "待补充"}
                </small>
                <div className="chips">
                  {(item.variants || []).map((variant: Json, index: number) => (
                    <span key={`${variant.sku || index}`}>
                      {variant.sku || "无SKU"} · {variant.price ? `${variant.currency || WORKSPACE.currency} ${variant.price}` : "价格待同步"} · 库存 {variant.inventory_quantity ?? "待同步"}
                    </span>
                  ))}
                </div>
                {item.shopify_url && (
                  <button className="live-link preview-link" type="button" onClick={() => previewShopify(String(item.shopify_url))}>
                    已上线 · 预览 Shopify 商品
                  </button>
                )}
                {item.last_error && <p className="error">最近同步错误：{item.last_error}</p>}
                {WORKSPACE.localized ? (
                  <div className="actions">
                    <button className="primary" onClick={() => openLocalization(item)}>
                      编辑{WORKSPACE.code === "FR" ? "法语" : "德语"}市场资料
                    </button>
                  </div>
                ) : (
                  user.permissions.includes("product.edit") && (
                    <div className="actions">
                      <button
                        className="secondary"
                        onClick={() => {
                          setCreating(false);
                          setEditing(product);
                        }}
                      >
                        编辑
                      </button>
                      <button className="secondary" onClick={() => save({ ...product, status: "review" })}>
                        送审
                      </button>
                      {user.permissions.includes("product.approve") && (
                        <button
                          className="primary"
                          onClick={() => {
                            setCreating(false);
                            setEditing({ ...product, status: "active" });
                          }}
                        >
                          批准并发布
                        </button>
                      )}
                    </div>
                  )
                )}
              </article>
            );
          })}
        </div>
      )}
      {!loading && !items.length && <div className="empty">暂无产品记录。可新建产品或从 Shopify 同步。</div>}
      {localizing && <MarketProductEditor item={localizing} user={user} onClose={() => setLocalizing(null)} onAction={localizationAction} />}
      <GoogleShoppingPanel user={user} />
    </section>
  );
}

function MarketReadinessPanel({ data, user, onReload, onMessage }: { data: Json; user: User; onReload: () => Promise<void>; onMessage: (value: string) => void }) {
  const [editing, setEditing] = useState<Json | null>(null);
  const [note, setNote] = useState("");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [marketId, setMarketId] = useState(String(data.publishing?.marketBinding?.shopify_market_id || ""));
  const [verifyingMarket, setVerifyingMarket] = useState(false);
  const gates = [
    ...(data.publishing?.launchGates || []).map((gate: Json) => ({
      ...gate,
      kind: "launch",
    })),
    ...(data.publishing?.productGates || []).map((gate: Json) => ({
      ...gate,
      kind: "product",
    })),
  ];
  async function save(status: string) {
    if (!editing) return;
    if (status === "verified" && !note.trim() && !evidenceRef.trim()) {
      onMessage("验证门槛必须填写核验说明或正式资料引用，不能空白标记完成。");
      return;
    }
    const path = editing.kind === "launch" ? `/workspace/launch-gates/${editing.requirement_key}` : `/workspace/compliance/${editing.product_code}/${editing.requirement_key}`;
    try {
      await api(path, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify({ status, note, evidenceRef }),
      });
      setEditing(null);
      setNote("");
      setEvidenceRef("");
      onMessage("法国市场门槛已更新并保留审核记录");
      await onReload();
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "门槛更新失败");
    }
  }
  async function seedFrance() {
    setSeeding(true);
    try {
      const result = await api<{ message?: string }>("/workspace/france/seed-localization-tasks", { method: "POST", headers: { "X-CSRF-Token": user.csrfToken } });
      onMessage(result.message || "法国本地化任务已创建");
    } catch (error) {
      onMessage(error instanceof Error ? error.message : "任务创建失败");
    } finally {
      setSeeding(false);
    }
  }
  async function saveAndVerifyMarket() {
    if (!marketId.trim()) { onMessage("请填写 Shopify France Market GID 或数字 ID"); return; }
    setVerifyingMarket(true);
    try {
      await api("/workspace/shopify-binding", { method: "PATCH", headers: { "Content-Type": "application/json", "X-CSRF-Token": user.csrfToken }, body: JSON.stringify({ shopifyMarketId: marketId }) });
      const result = await api<{ message?: string }>("/workspace/shopify-binding/verify", { method: "POST", headers: { "X-CSRF-Token": user.csrfToken } });
      onMessage(result.message || "Shopify Market 已远端回读");
      await onReload();
    } catch (error) { onMessage(error instanceof Error ? error.message : "Shopify Market 验证失败"); }
    finally { setVerifyingMarket(false); }
  }
  return (
    <section className="review-inbox connection-panel">
      <div className="card-row">
        <div>
          <span className="eyebrow">{WORKSPACE.marketLabel}正式开放门槛</span>
          <h2>{data.publishing?.allowed ? "已具备发布条件" : `仍有 ${data.publishing?.pendingRequirements?.length || 0} 项未验证`}</h2>
          <p>经营主体、法国消费者权利、Cookie、Shopify Market 与每款产品证据全部通过前，公开上架、结账和自动分发保持阻断。</p>
        </div>
        <Status value={data.publishing?.allowed ? "approved" : "blocked"} />
      </div>
      {WORKSPACE.code === "FR" && user.permissions.includes("task.create") && (
        <div className="actions">
          <button className="secondary" disabled={seeding} onClick={seedFrance}>
            {seeding ? "正在建立任务…" : "建立法国历史内容、产品与法律页本地化任务"}
          </button>
          <small>任务幂等；法律页缺少正式资料时只进入“等待补充”，不会生成占位内容。</small>
        </div>
      )}
      {user.permissions.includes("integration.manage") && (
        <div className="editor-card">
          <div className="card-row"><strong>Shopify {WORKSPACE.marketLabel} Market 绑定</strong><Status value={data.publishing?.marketBinding?.status || "unconfigured"} /></div>
          <label>Shopify Market GID / 数字 ID<input value={marketId} onChange={(event) => setMarketId(event.target.value)} placeholder="gid://shopify/Market/…" /></label>
          <p className="field-note">系统会从 Shopify 远端回读国家、EUR 和 {WORKSPACE.storefrontDomain} 法语根地址；仅保存 ID 不算验证完成。</p>
          <button className="secondary" disabled={verifyingMarket} onClick={saveAndVerifyMarket}>{verifyingMarket ? "正在远端验证…" : "保存并远端验证"}</button>
        </div>
      )}
      <div className="list-grid">
        {gates.map((gate: Json) => (
          <article className="record-card" key={`${gate.kind}-${gate.product_code || "market"}-${gate.requirement_key}`}>
            <div className="card-row">
              <span className="eyebrow">{gate.product_code || "全市场"}</span>
              <Status value={gate.status} />
            </div>
            <h3>{gate.label}</h3>
            <p>{gate.note || "尚未核验"}</p>
            {gate.evidence_url && <small>资料引用：{gate.evidence_url}</small>}
            {user.permissions.includes("product.approve") && (
              <button
                className="secondary"
                onClick={() => {
                  setEditing(gate);
                  setNote(gate.note || "");
                  setEvidenceRef(gate.evidence_url || "");
                }}
              >
                核验 / 修改
              </button>
            )}
          </article>
        ))}
      </div>
      {editing && (
        <div className="editor-card">
          <div className="card-row">
            <strong>
              {editing.product_code || "全市场"} · {editing.label}
            </strong>
            <button className="text-button" onClick={() => setEditing(null)}>
              关闭
            </button>
          </div>
          <label>
            核验说明
            <textarea value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <label>
            正式资料引用 / 编号
            <input value={evidenceRef} onChange={(event) => setEvidenceRef(event.target.value)} />
          </label>
          <div className="actions">
            <button className="primary" onClick={() => save("verified")}>
              标记已验证
            </button>
            <button className="secondary" onClick={() => save("missing")}>
              标记缺失
            </button>
            <button className="secondary" onClick={() => save("not_applicable")}>
              不适用（需说明）
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ProductEditor({ item, creating, canApprove, onCancel, onSave }: { item: Json; creating: boolean; canApprove: boolean; onCancel: () => void; onSave: (input: Json) => void }) {
  const [form, setForm] = useState<Json>(() => ({
    ...item,
    variants: (item.variants?.length
      ? item.variants
      : [
          {
            title: item.productTitle || item.name || "默认款",
            sku: item.sku || "",
            price: item.price || "",
            inventory: item.inventory ?? "",
            barcode: "",
            optionValuesText: "",
          },
        ]
    ).map((variant: Json) => ({
      ...variant,
      optionValuesText:
        variant.optionValuesText ??
        Object.entries(variant.optionValues || {})
          .map(([key, value]) => `${key}=${value}`)
          .join(", "),
    })),
  }));
  const [busy, setBusy] = useState(false);
  const change = (key: string, value: string) => setForm((current: Json) => ({ ...current, [key]: value }));
  const changeVariant = (index: number, key: string, value: unknown) =>
    setForm((current: Json) => ({
      ...current,
      variants: current.variants.map((variant: Json, variantIndex: number) => (variantIndex === index ? { ...variant, [key]: value } : variant)),
    }));
  const addVariant = () =>
    setForm((current: Json) => ({
      ...current,
      variants: [
        ...current.variants,
        {
          title: `款式 ${current.variants.length + 1}`,
          sku: "",
          price: "",
          inventory: "",
          barcode: "",
          optionValuesText: "",
        },
      ],
    }));
  const removeVariant = (index: number) =>
    setForm((current: Json) => ({
      ...current,
      variants: current.variants.filter((_: Json, variantIndex: number) => variantIndex !== index),
    }));
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    try {
      const variants = form.variants.map((variant: Json) => ({
        ...(variant.id ? { id: variant.id } : {}),
        title: variant.title || form.productTitle,
        sku: variant.sku,
        price: variant.price === "" ? null : variant.price,
        inventory: variant.inventory === "" ? null : Number(variant.inventory),
        barcode: variant.barcode || null,
        optionValues: parseOptionValues(variant.optionValuesText),
      }));
      await onSave({
        ...(creating ? {} : { id: form.id }),
        name: form.name,
        productTitle: form.productTitle,
        category: form.category,
        description: form.description,
        handle: form.handle,
        variants,
        status: form.status,
      });
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="editor-card" role="dialog" aria-modal="true" aria-label={creating ? "新增产品" : "编辑产品"} onSubmit={submit}>
      <div className="card-row">
        <strong>{creating ? "新增产品" : `编辑 ${item.name || item.product_code}`}</strong>
        <button className="text-button" type="button" onClick={onCancel}>
          关闭
        </button>
      </div>
      <div className="form-grid">
        <label>
          产品名称
          <input required value={form.name || ""} onChange={(event) => change("name", event.target.value)} />
        </label>
        <label>
          产品标题
          <input required value={form.productTitle || ""} onChange={(event) => change("productTitle", event.target.value)} placeholder="Shopify 对外标题" />
        </label>
        <label>
          分类
          <input required value={form.category || ""} onChange={(event) => change("category", event.target.value)} />
        </label>
        <label>
          链接 handle
          <input required value={form.handle || ""} onChange={(event) => change("handle", event.target.value)} placeholder="e8-ecg-smart-watch" />
        </label>
        <label>
          状态
          <select value={form.status || "draft"} onChange={(event) => change("status", event.target.value)}>
            <option value="draft">草稿</option>
            <option value="review">待审核</option>
            {canApprove && <option value="active">已批准并发布 Shopify</option>}
            <option value="blocked">阻断</option>
            <option value="archived">归档</option>
          </select>
        </label>
        <label className="wide">
          产品说明
          <textarea value={form.description || ""} onChange={(event) => change("description", event.target.value)} />
        </label>
      </div>
      <div className="card-row variant-heading">
        <div>
          <span className="eyebrow">Shopify 变体</span>
          <h3>SKU、价格、库存与选项</h3>
        </div>
        <button type="button" className="secondary" onClick={addVariant}>
          新增变体
        </button>
      </div>
      <div className="variant-editor">
        {form.variants.map((variant: Json, index: number) => (
          <fieldset key={variant.id || index}>
            <legend>变体 {index + 1}</legend>
            <label>
              名称
              <input required value={variant.title || ""} onChange={(event) => changeVariant(index, "title", event.target.value)} />
            </label>
            <label>
              SKU
              <input required value={variant.sku || ""} onChange={(event) => changeVariant(index, "sku", event.target.value.toUpperCase())} />
            </label>
            <label>
              价格（{WORKSPACE.currency}）
              <input inputMode="decimal" value={variant.price ?? ""} onChange={(event) => changeVariant(index, "price", event.target.value)} />
            </label>
            <label>
              库存
              <input inputMode="numeric" value={variant.inventory ?? ""} onChange={(event) => changeVariant(index, "inventory", event.target.value)} />
            </label>
            <label>
              条码 / GTIN
              <input value={variant.barcode || ""} onChange={(event) => changeVariant(index, "barcode", event.target.value)} />
            </label>
            <label>
              选项值
              <input value={variant.optionValuesText || ""} onChange={(event) => changeVariant(index, "optionValuesText", event.target.value)} placeholder="颜色=黑色, 尺寸=M" />
            </label>
            {form.variants.length > 1 && (
              <button type="button" className="danger-button" onClick={() => removeVariant(index)}>
                删除此变体
              </button>
            )}
          </fieldset>
        ))}
      </div>
      {form.variants.length > 1 && <p className="field-note">多变体必须为每个 SKU 填写相同选项名称，例如“颜色=黑色, 尺寸=M”。</p>}
      {form.status === "active" && <p className="field-note">保存后将使用已验证的 Shopify 授权写入全部变体、价格和库存，并发布到 Online Store；缺少 write_inventory 权限时会明确失败。</p>}
      <div className="actions">
        <button className="primary" disabled={busy}>
          {busy ? "正在保存…" : form.status === "active" ? "保存并发布" : "保存"}
        </button>
        <button className="secondary" type="button" onClick={onCancel}>
          取消
        </button>
      </div>
    </form>
  );
}

function parseOptionValues(value: unknown) {
  return Object.fromEntries(
    String(value || "")
      .split(/[\r\n,]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => {
        const index = item.indexOf("=");
        return index > 0 ? [item.slice(0, index).trim(), item.slice(index + 1).trim()] : ["", ""];
      })
      .filter(([key, option]) => key && option),
  );
}

function MarketProductEditor({ item, user, onClose, onAction }: { item: Json; user: User; onClose: () => void; onAction: (path: string, method: "PATCH" | "POST", body?: Json) => void }) {
  const [form, setForm] = useState<Json>({
    title: item.title || "",
    descriptionHtml: item.description_html || "",
    seoTitle: item.seo_title || "",
    seoDescription: item.seo_description || "",
    handle: item.handle || String(item.product_code || "").toLowerCase(),
    faq: JSON.stringify(item.faq_json || [], null, 2),
    imageAlt: JSON.stringify(item.image_alt_json || [], null, 2),
    evidenceIds: JSON.stringify(item.evidence_ids || [], null, 2),
  });
  const [returnNote, setReturnNote] = useState("");
  const change = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }));
  const languageLabel = WORKSPACE.code === "FR" ? "法语" : "德语";
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <section className="preview-dialog editor-window">
        <div className="card-row">
          <div>
            <span className="eyebrow">{WORKSPACE.marketLabel}市场产品本地化</span>
            <h3>
              {item.product_code} · {item.source_name}
            </h3>
            <p className="field-note">全球产品事实只读；此窗口只保存 {WORKSPACE.localeCode} 的标题、正文、SEO、FAQ、图片替代文本与证据。</p>
          </div>
          <button className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label className="wide">
            {languageLabel}产品标题
            <input value={form.title} onChange={(e) => change("title", e.target.value)} />
          </label>
          <label>
            {languageLabel} handle
            <input value={form.handle} onChange={(e) => change("handle", e.target.value)} />
          </label>
          <label>
            SEO 标题
            <input value={form.seoTitle} onChange={(e) => change("seoTitle", e.target.value)} />
          </label>
          <label className="wide">
            SEO 描述
            <textarea value={form.seoDescription} onChange={(e) => change("seoDescription", e.target.value)} />
          </label>
          <label className="wide">
            {languageLabel}产品正文 HTML
            <textarea rows={12} value={form.descriptionHtml} onChange={(e) => change("descriptionHtml", e.target.value)} />
          </label>
          <label className="wide">
            FAQ JSON 数组
            <textarea value={form.faq} onChange={(e) => change("faq", e.target.value)} />
          </label>
          <label className="wide">
            图片替代文本 JSON 数组
            <textarea value={form.imageAlt} onChange={(e) => change("imageAlt", e.target.value)} />
          </label>
          <label className="wide">
            证据 ID JSON 数组
            <textarea value={form.evidenceIds} onChange={(e) => change("evidenceIds", e.target.value)} />
          </label>
        </div>
        <p className="field-note">
          版本 v{item.version} · 状态 <Status value={item.status} /> · Shopify 源：{item.shopify_product_id || "未绑定"}
        </p>
        {item.shopify_last_error && <p className="error">最近发布错误：{item.shopify_last_error}</p>}
        <div className="actions">
          <button className="secondary" onClick={() => onAction("", "PATCH", form)}>
            保存新版本
          </button>
          {item.status === "draft" && (
            <button className="primary" onClick={() => onAction("/submit", "POST")}>
              提交审核
            </button>
          )}
          {item.status === "review" && user.permissions.includes("product.approve") && (
            <>
              <button className="primary" onClick={() => onAction("/decision", "POST", { decision: "approve" })}>
                批准
              </button>
              <input value={returnNote} onChange={(e) => setReturnNote(e.target.value)} placeholder="退回原因" />
              <button
                className="secondary"
                onClick={() =>
                  onAction("/decision", "POST", {
                    decision: "return",
                    note: returnNote,
                  })
                }
              >
                退回
              </button>
            </>
          )}
          {item.status === "approved" && user.permissions.includes("product.approve") && (
            <button className="primary" onClick={() => onAction("/publish", "POST")}>
              发布到 Shopify {WORKSPACE.marketLabel} Market
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function LegacyAssets({ user }: { user: User }) {
  const [data, setData] = useState<{ assets: Json[]; staged: Json[] }>({
    assets: [],
    staged: [],
  });
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [queue, setQueue] = useState<Json[]>([]);
  const [preview, setPreview] = useState<Json | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const load = () => api<{ assets: Json[]; staged: Json[] }>("/catalog/assets").then(setData);
  useEffect(() => {
    load().catch((e: Error) => setError(e.message));
  }, []);
  const visible = data.assets.filter((asset) => filter === "all" || asset.media_type === filter || asset.purpose === filter);
  const categories: Array<[string, string]> = [
    ["all", "全部"],
    ["image", "图片"],
    ["video", "视频"],
    ["audio", "音频"],
    ["document", "文档"],
    ["ugc", "UGC"],
    ["ad", "广告"],
    ["review", "评价"],
    ["packaging", "包装"],
    ["certificate", "证书"],
  ];
  async function upload(files: File[]) {
    const rows = files.slice(0, 20).map((file, index) => ({
      index: Date.now() + index,
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      status: "waiting",
      error: "",
    }));
    setQueue(rows);
    await runUploads(rows);
  }
  async function runUploads(rows: Json[]) {
    let cursor = 0;
    const worker = async () => {
      while (cursor < rows.length) {
        const row = rows[cursor++];
        if (!row) continue;
        setQueue((current) => current.map((item) => (item.index === row.index ? { ...item, status: "uploading", error: "" } : item)));
        try {
          await uploadFile(row.file, (progress) => setQueue((current) => current.map((item) => (item.index === row.index ? { ...item, progress } : item))), user.csrfToken);
          setQueue((current) => current.map((item) => (item.index === row.index ? { ...item, status: "done", progress: 100 } : item)));
        } catch (reason) {
          setQueue((current) =>
            current.map((item) =>
              item.index === row.index
                ? {
                    ...item,
                    status: "failed",
                    error: reason instanceof Error ? reason.message : "上传失败",
                  }
                : item,
            ),
          );
        }
      }
    };
    await Promise.all([worker(), worker()]);
    await load();
  }
  async function retry(index: number) {
    const row = queue.find((item) => item.index === index);
    if (row?.file) await runUploads([{ ...row, progress: 0, status: "waiting", error: "" }]);
  }
  return (
    <section className="page">
      <ModuleHeading kicker="素材权限与可用性" title="素材中心" copy="支持图片、视频、音频与文档的手工单个或批量添加；每个文件独立上传，失败不会撤销已成功项。" badge={`${data.assets.length} 已登记`} />
      {error && <p className="error">{error}</p>}
      <div className="asset-toolbar">
        <div className="filter-chips">
          {categories.map(([key, label]) => (
            <button className={filter === key ? "active" : ""} key={key} onClick={() => setFilter(key)}>
              {label} {key === "all" ? data.assets.length : data.assets.filter((asset) => asset.media_type === key || asset.purpose === key).length}
            </button>
          ))}
        </div>
        {user.permissions.includes("asset.edit") && (
          <button className="primary" onClick={() => input.current?.click()}>
            添加素材
          </button>
        )}
        <input
          ref={input}
          type="file"
          multiple
          hidden
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,audio/mpeg,audio/wav,application/pdf"
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            event.currentTarget.value = "";
            if (files.length) upload(files);
          }}
        />
      </div>
      {queue.length > 0 && (
        <section className="upload-queue">
          <h3>上传队列（最多 20 个，2 个并发）</h3>
          {queue.map((row) => (
            <div className="upload-row" key={row.index}>
              <div>
                <strong>{row.name}</strong>
                <small>
                  {(Number(row.size) / 1024 / 1024).toFixed(2)} MB · {row.status === "done" ? "成功" : row.status === "failed" ? row.error : row.status === "uploading" ? "上传中" : "等待上传"}
                </small>
              </div>
              <span className="upload-progress">
                <i style={{ width: `${row.progress}%` }} />
              </span>
              {row.status === "failed" && (
                <button className="secondary" onClick={() => retry(row.index)}>
                  重试
                </button>
              )}
            </div>
          ))}
        </section>
      )}
      <div className="asset-grid">
        {visible.map((asset) => (
          <button className="asset-card" key={asset.id} onClick={() => setPreview(asset)}>
            <span>{asset.media_type}</span>
            <strong>{asset.title}</strong>
            <small>
              {asset.purpose} · {formatBytes(asset.size_bytes)}
            </small>
            <Status value={asset.status} />
          </button>
        ))}
      </div>
      {!visible.length && <div className="empty">当前分类暂无素材</div>}
      <h2 className="section-title">V1 历史素材待校验</h2>
      <RecordTable rows={data.staged} columns={["title", "reason", "status"]} empty="没有待校验素材" />
      {preview && <AssetPreview asset={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}

function AssetPreview({ asset, onClose }: { asset: Json; onClose: () => void }) {
  const url = asset.preview_url;
  const media = asset.media_type === "image" ? <img src={url} alt={asset.title} /> : asset.media_type === "video" ? <video src={url} controls preload="metadata" /> : asset.media_type === "audio" ? <audio src={url} controls /> : asset.mime_type === "application/pdf" ? <iframe src={url} title={asset.title} /> : null;
  return (
    <div className="preview-overlay" role="dialog">
      <section className="preview-dialog">
        <div className="card-row">
          <strong>{asset.title}</strong>
          <button className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        {media || <p>此文件不支持内嵌预览。</p>}
        <p className="field-note">
          {asset.mime_type} · {formatBytes(asset.size_bytes)} · {asset.purpose}
        </p>
        {url && (
          <a className="primary" href={url} target="_blank" rel="noreferrer">
            打开或下载
          </a>
        )}
      </section>
    </div>
  );
}
function uploadFile(file: File, onProgress: (value: number) => void, csrfToken: string) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("file", file);
    xhr.open("POST", "/api/v2/catalog/assets/upload");
    xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      let payload: Json = {};
      try {
        payload = JSON.parse(xhr.responseText || "{}");
      } catch {
        payload = {};
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload.ok !== false) resolve();
      else reject(new Error(payload.message || payload.error || `上传失败 (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("网络中断"));
    xhr.send(form);
  });
}
function formatBytes(value: unknown) {
  const bytes = Number(value || 0);
  return bytes ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : "—";
}

function LegacyKnowledge() {
  const [rows, setRows] = useState<Json[]>([]);
  useEffect(() => {
    api<{ knowledge: Json[] }>("/catalog/knowledge")
      .then((v) => setRows(v.knowledge))
      .catch(() => {});
  }, []);
  return (
    <section className="page">
      <ModuleHeading kicker="证据与知识" title="知识中心" copy="知识来源、有效期和审核状态作为所有Agent的受控依据。" badge={`${rows.length} 条知识`} />
      <RecordTable rows={rows} columns={["title", "document_type", "source_label", "status", "valid_until"]} empty="暂无知识文档" />
    </section>
  );
}

function LegacyContent({ user, focusContentId }: { user: User; focusContentId: string | null }) {
  const [rows, setRows] = useState<Json[]>([]);
  const [products, setProducts] = useState<Json[]>([]);
  const [editing, setEditing] = useState<Json | null>(null);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("all");
  const load = () => api<{ content: Json[] }>("/catalog/content").then((v) => setRows(v.content));
  useEffect(() => {
    load().catch((e: Error) => setMessage(e.message));
    api<{ products: Json[] }>("/catalog/products")
      .then((v) => setProducts(v.products))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (focusContentId) edit(focusContentId);
  }, [focusContentId]);
  async function edit(id: string, openPreview = false) {
    try {
      const result = await api<{ content: Json }>(`/catalog/content/${id}`);
      setEditing({ ...result.content, __openPreview: openPreview });
      setCreating(false);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "无法读取内容");
    }
  }
  async function action(id: string, path: string, body: Json = {}) {
    try {
      const result = await api<{ message?: string }>(`/catalog/content/${id}/${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(body),
      });
      setMessage(result.message || "操作完成");
      await load();
      if (editing?.id === id) await edit(id);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "操作失败");
    }
  }
  async function save(input: Json) {
    try {
      const response = await api<{ content: Json; message?: string }>(input.id ? `/catalog/content/${input.id}` : "/catalog/content", {
        method: input.id ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(response.message || (input.contentKind === "video" ? "短视频任务已提交给本地 Codex" : "内容已保存"));
      setEditing(response.content);
      setCreating(false);
      await load();
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "保存失败");
    }
  }
  const filtered = rows.filter((row) => filter === "all" || row.content_kind === filter || row.channel === filter);
  const filters: Array<[string, string]> = [
    ["all", "全部"],
    ["video", "短视频"],
    ["article", "博客"],
    ["page", "Shopify页"],
  ];
  const start = (kind: "article" | "page" | "video") => {
    setCreating(true);
    setEditing({
      title: "",
      handle: "",
      content_kind: kind,
      category: kind === "video" ? "Short Video" : "General",
      body_json: { summaryHtml: "", bodyHtml: "", sceneImageUrls: [] },
      seo_json: { title: "", description: "" },
      risk_status: "review",
      shopify_blog_handle: "health-center",
      primary_product_id: "",
      product_code: "",
      targetPlatforms: [],
    });
  };
  return (
    <section className="page">
      <ModuleHeading kicker="内容版本与渠道" title="内容中心" copy="保留 V1 的编辑、预览、场景图和短视频分发包字段；短视频只提交本地 Codex 队列，出片后再人工审核。" badge={`${rows.length} 条内容`} />
      {message && <p className="message">{message}</p>}
      <div className="asset-toolbar">
        <div className="filter-chips">
          {filters.map(([key, label]) => (
            <button className={filter === key ? "active" : ""} key={key} onClick={() => setFilter(key)}>
              {label} {key === "all" ? rows.length : rows.filter((row) => row.content_kind === key || row.channel === key).length}
            </button>
          ))}
        </div>
        {user.permissions.includes("content.edit") && (
          <div className="actions">
            <button className="secondary" onClick={() => start("video")}>
              新建短视频
            </button>
            <button className="secondary" onClick={() => start("article")}>
              新建博客
            </button>
            <button className="primary" onClick={() => start("page")}>
              新建 Shopify 页
            </button>
          </div>
        )}
      </div>
      {(creating || editing) && (
        <ContentEditor
          item={editing!}
          creating={creating}
          products={products}
          onClose={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSave={save}
          onSubmitReview={(id) => action(id, "submit-review")}
          onPublish={(id) => action(id, "publish")}
        />
      )}
      <div className="list-grid">
        {filtered.map((row) => (
          <article className="record-card" key={row.id}>
            <div className="card-row">
              <span className="eyebrow">
                {row.content_kind === "page" ? "Shopify 页面" : row.content_kind === "video" ? "短视频" : "博客文章"} · {row.product_code || "未关联产品"}
              </span>
              <Status value={row.status} />
            </div>
            <h3>{row.title}</h3>
            <p>
              版本 {row.current_version} · 风险 {row.risk_status}
            </p>
            {row.content_kind === "video" && (
              <p className="field-note">
                本地 Codex：{row.video_task_status || "尚未提交"}
                {row.video_task_error ? ` · ${row.video_task_error}` : ""}
              </p>
            )}
            {row.shopify_url && (
              <a className="live-link" href={row.shopify_url} target="_blank" rel="noreferrer">
                已上线 · 预览
              </a>
            )}
            <div className="actions">
              <button className="secondary" onClick={() => edit(row.id)}>
                编辑
              </button>
              <button className="secondary" onClick={() => edit(row.id, true)}>
                预览
              </button>
              {user.permissions.includes("content.edit") && row.content_kind === "video" && (
                <button className="secondary" onClick={() => action(row.id, "generate-video")}>
                  提交本地 Codex
                </button>
              )}
              {user.permissions.includes("content.edit") && row.status === "draft" && (
                <button className="secondary" onClick={() => action(row.id, "submit-review")}>
                  提交审核
                </button>
              )}
              {user.permissions.includes("content.approve") && row.status === "approved" && row.content_kind !== "video" && (
                <>
                  <button className="primary" onClick={() => action(row.id, "publish")}>
                    发布到 Shopify
                  </button>
                  <button className="secondary" onClick={() => action(row.id, "publish", { adoptExisting: true })}>
                    接管同链接发布
                  </button>
                </>
              )}
            </div>
          </article>
        ))}
      </div>
      {filtered.length === 0 && <div className="empty">该分类暂无内容</div>}
    </section>
  );
}

function LegacyContentEditor({ item, creating, products, onClose, onSave, onSubmitReview, onPublish }: { item: Json; creating: boolean; products: Json[]; onClose: () => void; onSave: (input: Json) => void; onSubmitReview: (id: string) => void; onPublish: (id: string) => void }) {
  const body = item.body_json || {};
  const seo = item.seo_json || {};
  const [form, setForm] = useState(() => ({
    title: item.title || "",
    handle: item.handle || "",
    contentKind: item.content_kind || "article",
    category: item.category || "General",
    productId: item.primary_product_id || "",
    summaryHtml: body.summaryHtml || "",
    bodyHtml: body.bodyHtml || "",
    sceneImageUrls: Array.isArray(body.sceneImageUrls) ? body.sceneImageUrls.join("\n") : "",
    seoTitle: seo.title || item.title || "",
    seoDescription: seo.description || "",
    targetKeyword: seo.targetKeyword || "",
    riskStatus: item.risk_status || "review",
    blogHandle: item.shopify_blog_handle || "health-center",
    referenceVideoUrl: body.referenceVideoUrl || "",
    videoPrompt: body.videoPrompt || "",
    targetPlatforms: Array.isArray(body.targetPlatforms) ? body.targetPlatforms : Array.isArray(body.distributionPlatforms) ? body.distributionPlatforms : body.targetPlatform ? [body.targetPlatform] : [],
    videoDuration: body.videoDuration || body.durationSeconds || 30,
    aspectRatio: body.aspectRatio || "9:16",
    videoOutputUrl: body.videoOutputUrl || "",
    coverUrl: body.coverUrl || body.videoCoverUrl || "",
    landingUrl: body.landingUrl || "",
    disclosure: editorDisclosure(body.disclosure) || "AI-assisted product video. Review product specifications before ordering.",
    caption: body.caption || body.socialCaption || "",
    author: body.author || "",
    tags: Array.isArray(body.tags) ? body.tags.join(", ") : body.tags || "",
    evidence: typeof body.evidence === "string" ? body.evidence : JSON.stringify(body.evidence || [], null, 2),
    riskReasons: Array.isArray(body.riskReasons) ? body.riskReasons.join("\n") : body.riskReasons || "",
    internalLinks: typeof body.internalLinks === "string" ? body.internalLinks : JSON.stringify(body.internalLinks || [], null, 2),
  }));
  const [preview, setPreview] = useState(Boolean(item.__openPreview));
  const change = (key: string, value: any) => setForm((current) => ({ ...current, [key]: value }));
  const togglePlatform = (key: string) => change("targetPlatforms", form.targetPlatforms.includes(key) ? form.targetPlatforms.filter((value: string) => value !== key) : [...form.targetPlatforms, key]);
  return (
    <form
      className="editor-card"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ ...(creating ? {} : { id: item.id }), ...form });
      }}
    >
      <div className="card-row">
        <strong>{creating ? "新建内容" : `编辑内容 · v${item.current_version}`}</strong>
        <button className="text-button" type="button" onClick={onClose}>
          关闭
        </button>
      </div>
      {!creating && (
        <details className="content-history">
          <summary>版本与审核记录</summary>
          <p>
            历史版本：
            {Array.isArray(item.versions) ? item.versions.map((version: Json) => `v${version.version}`).join("、") : "暂无"}
          </p>
          {Array.isArray(item.reviews) && item.reviews.length > 0 && (
            <ul>
              {item.reviews.slice(0, 5).map((review: Json, index: number) => (
                <li key={`${review.created_at || index}`}>
                  {review.decision === "approve" ? "批准" : "退回"} · {review.note || "无说明"} · {format(review.created_at)}
                </li>
              ))}
            </ul>
          )}
        </details>
      )}
      <div className="form-grid">
        <label>
          内容类型
          <select value={form.contentKind} onChange={(event) => change("contentKind", event.target.value)}>
            <option value="video">短视频</option>
            <option value="article">博客文章</option>
            <option value="page">Shopify 页面</option>
          </select>
        </label>
        <label>
          标题
          <input required value={form.title} onChange={(event) => change("title", event.target.value)} />
        </label>
        <label>
          链接 handle
          <input required value={form.handle} onChange={(event) => change("handle", event.target.value)} />
        </label>
        <label>
          分类
          <input value={form.category} onChange={(event) => change("category", event.target.value)} />
        </label>
        <label>
          关联产品
          <select value={form.productId} onChange={(event) => change("productId", event.target.value)}>
            <option value="">未关联</option>
            {products.map((product) => (
              <option value={product.id} key={product.id}>
                {product.product_code} · {product.name}
              </option>
            ))}
          </select>
        </label>
        {form.contentKind === "article" && (
          <label>
            博客栏目
            <input value={form.blogHandle} onChange={(event) => change("blogHandle", event.target.value)} />
          </label>
        )}
        <label>
          目标关键词
          <input value={form.targetKeyword} onChange={(event) => change("targetKeyword", event.target.value)} />
        </label>
        <label>
          风险状态
          <select value={form.riskStatus} onChange={(event) => change("riskStatus", event.target.value)}>
            <option value="low">低风险</option>
            <option value="review">待审核</option>
            <option value="blocked">阻断</option>
          </select>
        </label>
        {form.contentKind !== "video" ? (
          <>
            <label className="wide">
              场景图 URL（最多 3 张，每行一个）
              <textarea value={form.sceneImageUrls} onChange={(event) => change("sceneImageUrls", event.target.value)} placeholder="https://.../scene-1.jpg" />
            </label>
            <label className="wide">
              摘要 HTML
              <textarea value={form.summaryHtml} onChange={(event) => change("summaryHtml", event.target.value)} />
            </label>
            <label className="wide">
              正文 HTML
              <textarea value={form.bodyHtml} onChange={(event) => change("bodyHtml", event.target.value)} />
            </label>
            <label>
              SEO 标题
              <input value={form.seoTitle} onChange={(event) => change("seoTitle", event.target.value)} />
            </label>
            <label className="wide">
              SEO 描述
              <textarea value={form.seoDescription} onChange={(event) => change("seoDescription", event.target.value)} />
            </label>
            <label>
              作者
              <input value={form.author} onChange={(event) => change("author", event.target.value)} />
            </label>
            <label>
              标签（逗号分隔）
              <input value={form.tags} onChange={(event) => change("tags", event.target.value)} />
            </label>
            <label className="wide">
              证据资料 JSON
              <textarea value={form.evidence} onChange={(event) => change("evidence", event.target.value)} />
            </label>
            <label className="wide">
              风险理由（每行一个）
              <textarea value={form.riskReasons} onChange={(event) => change("riskReasons", event.target.value)} />
            </label>
            <label className="wide">
              内链资料 JSON
              <textarea value={form.internalLinks} onChange={(event) => change("internalLinks", event.target.value)} />
            </label>
          </>
        ) : (
          <>
            <label className="wide">
              参考视频地址
              <input type="url" value={form.referenceVideoUrl} onChange={(event) => change("referenceVideoUrl", event.target.value)} />
            </label>
            <label className="wide">
              本地 Codex 提示词
              <textarea required value={form.videoPrompt} onChange={(event) => change("videoPrompt", event.target.value)} />
            </label>
            <label>
              时长（秒）
              <input inputMode="numeric" value={form.videoDuration} onChange={(event) => change("videoDuration", event.target.value)} />
            </label>
            <label>
              画幅
              <select value={form.aspectRatio} onChange={(event) => change("aspectRatio", event.target.value)}>
                <option value="9:16">9:16</option>
                <option value="1:1">1:1</option>
                <option value="16:9">16:9</option>
              </select>
            </label>
            <fieldset className="wide platform-checks">
              <legend>审核通过后分发目标</legend>
              {["facebook", "instagram", "tiktok", "youtube", "pinterest"].map((key) => (
                <label key={key}>
                  <input type="checkbox" checked={form.targetPlatforms.includes(key)} onChange={() => togglePlatform(key)} />
                  {key}
                </label>
              ))}
            </fieldset>
            <label className="wide">
              成片地址
              <input type="url" value={form.videoOutputUrl} onChange={(event) => change("videoOutputUrl", event.target.value)} />
            </label>
            <label className="wide">
              封面地址
              <input type="url" value={form.coverUrl} onChange={(event) => change("coverUrl", event.target.value)} />
            </label>
            <label className="wide">
              商品落地页
              <input type="url" value={form.landingUrl} onChange={(event) => change("landingUrl", event.target.value)} />
            </label>
            <label className="wide">
              统一社媒正文
              <textarea value={form.caption} onChange={(event) => change("caption", event.target.value)} />
            </label>
            <label className="wide">
              必要披露
              <textarea value={form.disclosure} onChange={(event) => change("disclosure", event.target.value)} />
            </label>
          </>
        )}
      </div>
      {preview && <ContentPreview form={form} />}
      {form.contentKind === "video" && <p className="field-note">变更短视频提示词、参考素材、时长、画幅或场景图时，才会创建 VIDEO_FACTORY_PROJECT 本地 Codex 队列；未填写成片、封面、平台和披露前不能批准。</p>}
      <div className="actions">
        <button className="primary">保存版本</button>
        <button className="secondary" type="button" onClick={() => setPreview(!preview)}>
          {preview ? "关闭预览" : "预览"}
        </button>
        {!creating && item.status === "draft" && (
          <button className="secondary" type="button" onClick={() => onSubmitReview(item.id)}>
            提交审核
          </button>
        )}
        {!creating && item.status === "approved" && form.contentKind !== "video" && (
          <button className="primary" type="button" onClick={() => onPublish(item.id)}>
            发布到 Shopify
          </button>
        )}
        <button className="secondary" type="button" onClick={onClose}>
          取消
        </button>
      </div>
    </form>
  );
}

function editorDisclosure(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value))
    return value
      .map((item) => editorDisclosure(item))
      .filter(Boolean)
      .join("\n");
  if (value && typeof value === "object") return JSON.stringify(value);
  return "";
}
function ContentPreview({ form }: { form: Json }) {
  const images = String(form.sceneImageUrls || "")
    .split(/\r?\n|,/)
    .map((url) => url.trim())
    .filter(Boolean)
    .slice(0, 3);
  if (form.contentKind === "video")
    return (
      <section className="content-preview">
        <h3>{form.title}</h3>
        {form.videoOutputUrl ? <video controls preload="metadata" poster={form.coverUrl} src={form.videoOutputUrl} /> : <p>成片尚未生成。</p>}
        <p>
          <strong>参考视频：</strong>
          {form.referenceVideoUrl ? (
            <a href={form.referenceVideoUrl} target="_blank" rel="noreferrer">
              打开参考视频
            </a>
          ) : (
            "未填写"
          )}
        </p>
        <p>
          <strong>分发平台：</strong>
          {form.targetPlatforms?.join("、") || "未选择"}
        </p>
        <p>
          <strong>社媒正文：</strong>
          {form.caption || "未填写"}
        </p>
        <p>
          <strong>提示词：</strong>
          {form.videoPrompt || "未填写"}
        </p>
        <p>
          <strong>必要披露：</strong>
          {form.disclosure || "未填写"}
        </p>
      </section>
    );
  return (
    <section className="content-preview">
      <h3>{form.title}</h3>
      {images.length > 0 && (
        <div className="scene-grid">
          {images.map((url) => (
            <figure key={url}>
              <img src={url} alt={form.title} />
              <figcaption>内容场景图</figcaption>
            </figure>
          ))}
        </div>
      )}
      <article className="article-preview">
        <div
          dangerouslySetInnerHTML={{
            __html: `${form.summaryHtml || ""}<hr>${form.bodyHtml || "<p>正文尚未生成。</p>"}`,
          }}
        />
      </article>
    </section>
  );
}

function Reviews({ user, onOpenRecord }: { user: User; onOpenRecord: (type: string, id: string, mode?: "preview" | "edit") => void }) {
  return (
    <section className="page">
      <ModuleHeading kicker="全业务人工审核" title="审核中心" copy="统一汇总内容、产品、素材、知识、AI 任务与增长建议；按风险和等待时间排序，并保留批准或退回记录。" badge="版本锁定" />
      <ReviewCenterPanel user={user} onOpen={onOpenRecord} />
    </section>
  );
}

function Metrics({ user }: { user: User }) {
  const [data, setData] = useState<{
    sources: Json[];
    metrics: Json[];
    commerce: Json;
  }>({ sources: [], metrics: [], commerce: {} });
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      setData(await api<{ sources: Json[]; metrics: Json[]; commerce: Json }>("/catalog/data-center"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load().catch((error: Error) => setMessage(error.message));
  }, []);
  async function sync(provider: string) {
    setBusy(provider);
    setMessage("");
    try {
      const result = await api<{ message?: string }>(`/catalog/data-center/${provider}/sync`, { method: "POST", headers: { "X-CSRF-Token": user.csrfToken } });
      setMessage(result.message || "数据已同步");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "数据同步失败");
      await load().catch(() => {});
    } finally {
      setBusy("");
    }
  }
  const commerce = data.commerce || {};
  const support = commerce.support || {};
  return (
    <section className="page">
      <ModuleHeading kicker="授权数据事实源" title="数据中心" copy="Shopify、Klaviyo 与其他已连接工具的最新同步数据都在这里展示；未配置或未授权的数据会明确标注，不会补零或伪造。" badge={loading ? "正在加载" : `${data.metrics.length} 条最新指标`} />
      {message && <p className="message">{message}</p>}
      {loading && <div className="empty loading-state">正在读取授权数据…</div>}
      {!loading && (
        <>
          <div className="data-summary-grid">
            <article>
              <small>Shopify 订单</small>
              <strong>{commerce.orderTotal ?? "—"}</strong>
              <span>最近同步：{format(commerce.lastOrderSync)}</span>
            </article>
            <article>
              <small>客户档案</small>
              <strong>{commerce.customerTotal ?? "—"}</strong>
              <span>仅保存授权范围内的必要数据</span>
            </article>
            <article>
              <small>客服工单</small>
              <strong>{support.total ?? "—"}</strong>
              <span>
                {support.open ?? "—"} 项待处理 · {support.note || "等待连接"}
              </span>
            </article>
          </div>
          <h2 className="section-title">同步工具数据</h2>
          <div className="list-grid data-source-grid">
            {data.sources.map((source) => (
              <article className="record-card" key={source.key}>
                <div className="card-row">
                  <span className="eyebrow">{source.category}</span>
                  <Status value={source.status} />
                </div>
                <h3>{source.display_name}</h3>
                <p>{source.status_note || "等待配置"}</p>
                <small>
                  最近验证：{format(source.last_checked_at)} · 最近同步：
                  {format(source.last_synced_at)}
                </small>
                {source.last_sync_error && <p className="field-note">最近失败：{source.last_sync_error}</p>}
                {Array.isArray(source.metrics) && source.metrics.length > 0 ? (
                  <div className="metric-list">
                    {source.metrics.map((metric: Json) => (
                      <div key={`${metric.source}-${metric.metric_key}`}>
                        <span>{metricLabel(metric.metric_key)}</span>
                        <strong>{metricValue(metric)}</strong>
                        <small>{metric.text_value || format(metric.synced_at)}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="field-note">{source.status === "connected" ? "已连接，尚未产生可展示的同步数据。" : "未配置或未获得数据读取授权。"}</p>
                )}
                {source.sync_capable && user.permissions.includes("analytics.read") && (
                  <div className="actions">
                    <button className="secondary" disabled={busy === source.key} onClick={() => sync(source.key)}>
                      {busy === source.key ? "正在同步…" : "同步数据"}
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
          <h2 className="section-title">最近 Shopify 订单</h2>
          <RecordTable rows={commerce.recentOrders || []} columns={["order_number", "total_amount", "currency", "financial_status", "fulfillment_status", "source_updated_at"]} empty="尚未从 Shopify 同步到订单明细。" />
          <h2 className="section-title">全部最新指标</h2>
          <RecordTable
            rows={data.metrics.map((metric) => ({
              ...metric,
              numeric_value: metricValue(metric),
            }))}
            columns={["source", "metric_key", "numeric_value", "text_value", "currency", "synced_at"]}
            empty="暂无可用指标"
          />
        </>
      )}
    </section>
  );
}

function metricLabel(value: unknown) {
  const labels: Record<string, string> = {
    orders_total: "订单总数",
    revenue: "近30天营收",
    customers_total: "客户总数",
    repeat_rate: "复购率",
    klaviyo_profiles: "Klaviyo 客户档案",
    klaviyo_metrics: "Klaviyo 事件指标",
    "klaviyo.profiles.total": "Klaviyo 客户档案",
    "klaviyo.metrics.total": "Klaviyo 事件指标",
    "ga4.sessions": "GA4 会话",
    "ga4.active_users": "GA4 活跃用户",
    "ga4.key_events": "GA4 关键事件",
    "ga4.revenue": "GA4 收入",
    "search.clicks": "自然搜索点击",
    "search.impressions": "自然搜索展示",
    "search.ctr": "自然搜索 CTR",
    "search.position": "自然搜索平均排名",
    "ads.impressions": "广告展示",
    "ads.clicks": "广告点击",
    "ads.cost": "广告花费",
    "ads.conversions": "广告转化",
    "ads.conversion_value": "广告转化价值",
    "youtube.subscribers": "YouTube 订阅",
    "youtube.views": "YouTube 观看",
    "youtube.videos": "YouTube 视频数",
    "merchant.catalog.connection": "Merchant 商品目录连接",
  };
  const key = String(value || "");
  return labels[key] || key.replaceAll("_", " ");
}
function metricValue(metric: Json) {
  if (metric.numeric_value === null || metric.numeric_value === undefined || metric.numeric_value === "") return metric.text_value || "—";
  const value = Number(metric.numeric_value);
  if (!Number.isFinite(value)) return String(metric.numeric_value);
  if (metric.metric_key === "search.ctr" || metric.metric_key === "repeat_rate") return `${new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2 }).format(metric.metric_key === "search.ctr" ? value * 100 : value)}%`;
  const formatted = new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);
  return metric.currency ? `${formatted} ${metric.currency}` : formatted;
}

const GOOGLE_CONNECTION_KEYS = ["ga4", "google_merchant", "google_ads", "youtube", "search_console"];

function Integrations({ user }: { user: User }) {
  const [rows, setRows] = useState<Json[]>([]);
  const [google, setGoogle] = useState<Json | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState("");
  const [configuringGoogle, setConfiguringGoogle] = useState(false);
  const [configuringProvider, setConfiguringProvider] = useState<Json | null>(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const [integrationData, googleData] = await Promise.all([api<{ integrations: Json[] }>("/integrations"), api<Json>("/integrations/google/setup")]);
      setRows(integrationData.integrations);
      setGoogle(googleData);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load().catch((error: Error) => setMessage(error.message));
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("google_oauth");
    const note = params.get("google_message");
    if (status) {
      setMessage(note || (status === "connected" ? "Google 授权已完成，请逐项验证账号或属性" : "Google 授权未完成"));
      window.history.replaceState({}, "", window.location.pathname);
      load().catch(() => {});
    }
  }, []);
  async function verify(key: string) {
    setBusy(key);
    setMessage("");
    try {
      const path = GOOGLE_CONNECTION_KEYS.includes(key) ? `/integrations/google/${key}/verify` : `/integrations/${key}/verify`;
      const result = await api<{ message: string }>(path, {
        method: "POST",
        headers: { "X-CSRF-Token": user.csrfToken },
      });
      setMessage(result.message);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "验证失败");
      await load().catch(() => {});
    } finally {
      setBusy("");
    }
  }
  async function saveGoogle(input: Json) {
    setBusy("google-config");
    try {
      const result = await api<Json>("/integrations/google/configuration", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(result.message || "Google OAuth 设置已保存");
      setConfiguringGoogle(false);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Google OAuth 设置保存失败");
    } finally {
      setBusy("");
    }
  }
  async function authorizeGoogle() {
    setBusy("google-authorize");
    try {
      const result = await api<{ authorizationUrl: string }>("/integrations/google/authorize", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify({ providers: GOOGLE_CONNECTION_KEYS }),
      });
      window.location.assign(result.authorizationUrl);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法开始 Google 授权");
      setBusy("");
    }
  }
  async function saveProvider(provider: Json, input: Json) {
    setBusy(`provider-${provider.key}`);
    try {
      const result = await api<Json>(`/integrations/google/${provider.key}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(result.message || "账号设置已保存");
      setConfiguringProvider(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "账号设置保存失败");
    } finally {
      setBusy("");
    }
  }
  const googleRows = rows.filter((row) => GOOGLE_CONNECTION_KEYS.includes(String(row.key)));
  const otherRows = rows.filter((row) => !GOOGLE_CONNECTION_KEYS.includes(String(row.key)));
  return (
    <section className="page">
      <ModuleHeading kicker="最小权限连接" title="连接与系统" copy="所有 Google 授权均在服务器端加密保存。只有账号、属性和 Scope 均通过实测后才显示“已连接”；本地 Codex 不持有外部发布凭据。" badge={loading ? "正在加载" : `${rows.filter((row) => row.status === "connected").length} 已连接`} />
      {message && <p className="message">{message}</p>}
      {loading && <div className="empty loading-state">正在读取连接状态…</div>}
      {!loading && (
        <>
          {google && (
            <section className="review-inbox connection-panel">
              <div className="card-row">
                <div>
                  <span className="eyebrow">统一 Google 数据与渠道授权</span>
                  <h2>Google Analytics、Merchant、Ads、YouTube 与自然搜索</h2>
                  <p>使用一个 Google OAuth Web Client，一次授权后按服务分别选择 GA4 属性、Merchant 账号、Ads 客户账号、YouTube 频道和 Search Console 站点。</p>
                </div>
                <Status value={google.authorized ? "connected" : google.oauthConfigured ? "configured_untested" : "unconfigured"} />
              </div>
              <div className="connection-meta">
                <span>
                  回调地址：<code>{google.callbackUrl}</code>
                </span>
                <span>OAuth：{google.oauthConfigured ? "已配置" : "未配置"}</span>
                <span>Google 授权：{google.authorized ? "已完成" : "未完成"}</span>
                <span>
                  Ads Developer Token：
                  {google.googleAdsDeveloperTokenConfigured ? "已配置" : "未配置"}
                </span>
              </div>
              <div className="actions">
                <button className="secondary" onClick={() => setConfiguringGoogle(true)}>
                  配置 Google OAuth
                </button>
                <button className="primary" disabled={!google.oauthConfigured || busy === "google-authorize"} onClick={authorizeGoogle}>
                  {busy === "google-authorize" ? "正在跳转…" : "连接 Google 账号"}
                </button>
              </div>
            </section>
          )}
          <h2 className="section-title">Google 服务</h2>
          <div className="list-grid">
            {googleRows.map((row) => (
              <article className="record-card" key={row.key}>
                <div className="card-row">
                  <span className="eyebrow">{row.category}</span>
                  <Status value={row.status} />
                </div>
                <h3>{row.display_name}</h3>
                <p>{row.status_note || "等待配置"}</p>
                <small>
                  最近验证：{format(row.last_checked_at)} · Scope：
                  {Array.isArray(row.scopes) && row.scopes.length ? row.scopes.join("、") : "待验证"}
                </small>
                {Array.isArray(google?.providers) && google.providers.find((item: Json) => item.key === row.key)?.configuredAccount && (
                  <p className="field-note">
                    当前账号/属性：
                    {google.providers.find((item: Json) => item.key === row.key)?.configuredAccount}
                  </p>
                )}
                <div className="actions">
                  <button className="secondary" onClick={() => setConfiguringProvider(row)}>
                    设置账号 / 属性
                  </button>
                  <button className="primary" disabled={!google?.authorized || busy === row.key} onClick={() => verify(String(row.key))}>
                    {busy === row.key ? "正在验证…" : "验证连接"}
                  </button>
                </div>
              </article>
            ))}
          </div>
          <h2 className="section-title">其他连接</h2>
          <div className="list-grid">
            {otherRows
              .filter((row) => !["meta", "tiktok", "pinterest", "postiz"].includes(String(row.key)))
              .map((row) => (
                <article className="record-card" key={row.key}>
                  <div className="card-row">
                    <span className="eyebrow">{row.category}</span>
                    <Status value={row.status} />
                  </div>
                  <h3>{row.display_name}</h3>
                  <p>{row.status_note || "等待配置"}</p>
                  <small>
                    最近验证：{format(row.last_checked_at)} · Scope：
                    {Array.isArray(row.scopes) && row.scopes.length ? row.scopes.join("、") : "待验证"}
                  </small>
                  {row.migrated && <p className="field-note">● 已迁移加密授权材料</p>}
                  {row.migrated && (
                    <div className="actions">
                      <button className="secondary" disabled={busy === row.key} onClick={() => verify(String(row.key))}>
                        {busy === row.key ? "正在验证…" : "验证连接"}
                      </button>
                    </div>
                  )}
                </article>
              ))}
          </div>
          {rows.length === 0 && <div className="empty">暂无连接配置。</div>}
          <KlaviyoMarketConnection user={user} />
          <PostizConnections user={user} />
          <SocialConnections user={user} />
          {configuringGoogle && <GoogleOAuthWindow setup={google || {}} busy={busy === "google-config"} onClose={() => setConfiguringGoogle(false)} onSave={saveGoogle} />}
          {configuringProvider && <GoogleProviderWindow provider={configuringProvider} setup={google || {}} busy={busy === `provider-${configuringProvider.key}`} onClose={() => setConfiguringProvider(null)} onSave={(input) => saveProvider(configuringProvider, input)} />}
        </>
      )}
    </section>
  );
}

function GoogleOAuthWindow({ setup, busy, onClose, onSave }: { setup: Json; busy: boolean; onClose: () => void; onSave: (input: Json) => void }) {
  const [form, setForm] = useState<Json>({
    clientId: "",
    clientSecret: "",
    googleAdsDeveloperToken: "",
    googleAdsLoginCustomerId: setup.googleAdsLoginCustomerId || "",
  });
  const change = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">Google Cloud Web Client</span>
            <h3>配置统一 Google 授权</h3>
            <p className="field-note">在 Google Cloud 的 OAuth Web Client 中添加下方回调地址，并启用 Analytics Data/Admin、Search Console、Merchant、YouTube Data 与 Google Ads API。Client Secret 不会回显，留空会保留已有值。</p>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label className="wide">
            OAuth 回调地址
            <input readOnly value={setup.callbackUrl || ""} />
          </label>
          <label className="wide">
            Google OAuth Client ID
            <input required={!setup.oauthConfigured} value={form.clientId} onChange={(event) => change("clientId", event.target.value)} placeholder={setup.oauthConfigured ? "已配置；留空不修改" : "...apps.googleusercontent.com"} />
          </label>
          <label className="wide">
            Google OAuth Client Secret
            <input required={!setup.oauthConfigured} type="password" autoComplete="new-password" value={form.clientSecret} onChange={(event) => change("clientSecret", event.target.value)} placeholder={setup.oauthConfigured ? "已配置；留空不修改" : "Google Cloud OAuth Client Secret"} />
          </label>
          <label>
            Google Ads Developer Token
            <input value={form.googleAdsDeveloperToken} onChange={(event) => change("googleAdsDeveloperToken", event.target.value)} placeholder="来自 Google Ads API Center" />
          </label>
          <label>
            Google Ads 管理账号 ID（可选）
            <input value={form.googleAdsLoginCustomerId} onChange={(event) => change("googleAdsLoginCustomerId", event.target.value)} placeholder="123-456-7890" />
          </label>
        </div>
        <div className="actions">
          <button className="primary" disabled={busy}>
            {busy ? "正在保存…" : "加密保存设置"}
          </button>
          <button className="secondary" type="button" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function GoogleProviderWindow({ provider, setup, busy, onClose, onSave }: { provider: Json; setup: Json; busy: boolean; onClose: () => void; onSave: (input: Json) => void }) {
  const linked = Array.isArray(setup.providers) ? setup.providers.find((item: Json) => item.key === provider.key) || {} : {};
  const account = linked.configuredAccount || "";
  const [value, setValue] = useState(String(account));
  const accounts = Array.isArray(linked.accounts) ? linked.accounts : [];
  const spec: Record<string, { field: string; label: string; placeholder: string; help: string }> = {
    ga4: {
      field: "propertyId",
      label: "GA4 Property ID",
      placeholder: "例如 123456789",
      help: "先点击验证连接读取可访问属性；再选择或填写需要同步的 GA4 Property ID。",
    },
    google_merchant: {
      field: "merchantAccountId",
      label: "Merchant Center Account ID",
      placeholder: "数字账号 ID",
      help: "填写 Merchant Center 内的数字账号 ID；Google Shopping 商品源仍以 Shopify Google & YouTube 渠道为准。",
    },
    google_ads: {
      field: "googleAdsCustomerId",
      label: "Google Ads 客户账号 ID",
      placeholder: "123-456-7890",
      help: "需要已保存的 Google Ads Developer Token；验证会检查该客户账号是否在当前授权范围内。",
    },
    youtube: {
      field: "youtubeChannelId",
      label: "YouTube Channel ID",
      placeholder: "UC…",
      help: "验证会读取频道；后续视频发布继续遵循平台审核和内容审核边界。",
    },
    search_console: {
      field: "siteUrl",
      label: "Search Console 站点",
      placeholder: "sc-domain:saydian.com",
      help: "可填写 sc-domain:saydian.com 或完整 URL-prefix 站点。",
    },
  };
  const current = spec[String(provider.key)] || spec.ga4!;
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({ [current.field]: value });
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">{provider.display_name}</span>
            <h3>设置账号 / 属性</h3>
            <p className="field-note">{current.help}</p>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label className="wide">
            {current.label}
            <input list={`google-accounts-${provider.key}`} value={value} onChange={(event) => setValue(event.target.value)} placeholder={current.placeholder} />
            <datalist id={`google-accounts-${provider.key}`}>
              {accounts.map((item: Json) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </datalist>
          </label>
          {accounts.length > 0 && (
            <p className="field-note wide">
              已发现：
              {accounts.map((item: Json) => `${item.name}（${item.id}）`).join("；")}
            </p>
          )}
        </div>
        <div className="actions">
          <button className="primary" disabled={busy}>
            {busy ? "正在保存…" : "保存设置"}
          </button>
          <button className="secondary" type="button" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function ModuleHeading({ kicker, title, copy, badge }: { kicker: string; title: string; copy: string; badge: string }) {
  return (
    <div className="module-heading">
      <div>
        <span>{kicker}</span>
        <h2>{title}</h2>
        <p>{copy}</p>
      </div>
      <strong>{badge}</strong>
    </div>
  );
}
function RecordTable({ rows, columns, empty }: { rows: Json[]; columns: string[]; empty: string }) {
  if (!rows.length) return <div className="empty">{empty}</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column.replaceAll("_", " ")}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || row.legacy_id || index}>
              {columns.map((column) => (
                <td key={column}>{format(row[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const STATUS_LABELS: Record<string, string> = {
  active: "已启用",
  approved: "已批准",
  archived: "已归档",
  blocked: "已阻断",
  completed: "已完成",
  connected: "已连接",
  configured_untested: "已配置待验证",
  dead_letter: "处理失败",
  draft: "草稿",
  failed: "执行失败",
  pending: "待处理",
  published: "已发布",
  queued: "排队中",
  rejected: "已退回",
  review: "待审核",
  running: "执行中",
  unconfigured: "未配置",
  waiting_input: "等待补充",
};
function Status({ value }: { value: unknown }) {
  const raw = String(value || "unknown");
  return <b className={`status status-${raw}`}>{STATUS_LABELS[raw] || format(value)}</b>;
}
function format(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  const text = String(value);
  if (/^-?\d+\.\d{3,}$/.test(text)) return new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2 }).format(Number(text));
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString("zh-CN", { hour12: false });
  }
  return text;
}

function AgentCenter({ agents, user, focusAgentKey, focusTaskId }: { agents: AgentState[]; user: User; focusAgentKey: string | null; focusTaskId: string | null }) {
  return (
    <section className="page">
      <ModuleHeading kicker="本地 Codex 执行" title="七个 Agent 统一任务中心" copy="从创建、领取、补充输入、重试、审核到结果落库全程留痕；不会把外部发布凭据交给本地 Codex。" badge="真实任务队列" />
      <TaskOperations agents={agents} user={user} focusAgentKey={focusAgentKey} focusTaskId={focusTaskId} />
    </section>
  );
}
function AgentCard({ agent, actionable = false, csrf, onOpen, focused = false }: { agent: AgentState; actionable?: boolean; csrf?: string; onOpen?: () => void; focused?: boolean }) {
  const [message, setMessage] = useState("");
  async function run() {
    setMessage("提交中…");
    try {
      await api(`/agents/${agent.key}/runs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf || "",
        },
        body: JSON.stringify({
          taskType: `manual_${agent.key}`,
          productCodes: ["E8", "R7", "W9S"],
          input: { requestedFrom: "v2_agent_center" },
        }),
      });
      setMessage("已进入本地Codex队列");
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "提交失败");
    }
  }
  return (
    <article
      id={`agent-${agent.key}`}
      className={`agent-card${onOpen ? " agent-card-link" : ""}${focused ? " is-focused" : ""}`}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (onOpen && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="agent-top">
        <span>{agent.executionLane === "video" ? "VIDEO" : agent.executionLane === "intelligence" ? "DATA" : "CODEX"}</span>
        <i className={agent.state?.enabled === false ? "off" : ""} />
      </div>
      <h3>{agent.name}</h3>
      <p>{agent.purpose}</p>
      <div className="agent-meta">
        <span>每日目标：{agent.dailyTarget ?? "事件触发"}</span>
        <span>{agent.requiresApproval ? "需人工审核" : "只读分析"}</span>
      </div>
      {agent.state && (
        <div className="queue">
          <span>排队 {agent.state.queued}</span>
          <span>执行 {agent.state.running}</span>
          <span>待审 {agent.state.review}</span>
        </div>
      )}
      {actionable && (
        <button className="secondary" onClick={run}>
          手工运行
        </button>
      )}
      {message && <small className="message">{message}</small>}
    </article>
  );
}
const ASSET_PURPOSES: Array<[string, string]> = [
  ["general", "通用素材"],
  ["ugc", "UGC"],
  ["ad", "广告"],
  ["review", "评价"],
  ["packaging", "包装"],
  ["certificate", "证书"],
];
const ASSET_CATEGORIES: Array<[string, string]> = [["all", "全部"], ["image", "图片"], ["video", "视频"], ["audio", "音频"], ["document", "文档"], ...ASSET_PURPOSES.slice(1)];

function Assets({ user, focusTarget }: { user: User; focusTarget?: FocusTarget | null }) {
  const [data, setData] = useState<{ assets: Json[]; staged: Json[] }>({
    assets: [],
    staged: [],
  });
  const [products, setProducts] = useState<Json[]>([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [market, setMarket] = useState("all");
  const [status, setStatus] = useState("all");
  const [queue, setQueue] = useState<Json[]>([]);
  const [uploading, setUploading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [editing, setEditing] = useState<Json | null>(null);
  const [preview, setPreview] = useState<Json | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteAsset, setDeleteAsset] = useState<Json | null>(null);
  const [assetDecision, setAssetDecision] = useState<{
    asset: Json;
    decision: "approve" | "return";
  } | null>(null);
  const [assetReviewBusy, setAssetReviewBusy] = useState(false);
  const [uploadForm, setUploadForm] = useState<Json>({
    purpose: "general",
    marketCode: WORKSPACE.marketCode,
    productId: "",
    description: "",
    tags: "",
  });
  const input = useRef<HTMLInputElement>(null);
  const load = async () => {
    setLoading(true);
    try {
      const [assets, productData] = await Promise.all([api<{ assets: Json[]; staged: Json[] }>("/catalog/assets"), api<{ products: Json[] }>("/catalog/products")]);
      setData(assets);
      setProducts(productData.products);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load().catch((error: Error) => setMessage(error.message));
  }, []);
  useEffect(() => {
    if (!focusTarget || !data.assets.length) return;
    const asset = data.assets.find((item) => String(item.id) === focusTarget.id);
    if (!asset) {
      setMessage("未找到需要审核的素材记录，可能已被删除或转入历史库。");
      return;
    }
    if (focusTarget.mode === "edit" && user.permissions.includes("asset.edit")) setEditing(asset);
    else setPreview(asset);
    document.getElementById(`asset-${asset.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusTarget, data.assets, user.permissions]);
  const markets = [...new Set(data.assets.map((asset) => String(asset.market_code || "通用")))];
  const visible = data.assets.filter((asset) => (filter === "all" || asset.media_type === filter || asset.purpose === filter) && (market === "all" || String(asset.market_code || "通用") === market) && (status === "all" || asset.status === status) && `${asset.title || ""} ${(asset.tags || []).join(" ")} ${asset.description || ""} ${asset.product_code || ""} ${asset.source_label || ""}`.toLowerCase().includes(query.toLowerCase()));
  const changeUpload = (key: string, value: unknown) => setUploadForm((current) => ({ ...current, [key]: value }));
  async function uploadFiles(files: File[]) {
    if (!files.length) return;
    if (files.length > 20) {
      setMessage("一次最多选择 20 个文件");
      return;
    }
    if (files.some((file) => file.size > 100 * 1024 * 1024)) {
      setMessage("单个文件不能超过 100MB");
      return;
    }
    const metadata = { ...uploadForm };
    const rows = files.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      status: "waiting",
      error: "",
      metadata,
    }));
    setQueue(rows);
    setUploading(false);
    await runUploadQueue(rows, metadata);
  }
  async function runUploadQueue(rows: Json[], metadata: Json) {
    let cursor = 0;
    const worker = async () => {
      while (cursor < rows.length) {
        const row = rows[cursor++];
        if (!row) continue;
        row.status = "uploading";
        row.error = "";
        setQueue((current) => current.map((entry) => (entry.id === row.id ? { ...entry, status: "uploading", error: "" } : entry)));
        try {
          await uploadAssetFile(row.file, metadata, (progress) => setQueue((current) => current.map((entry) => (entry.id === row.id ? { ...entry, progress } : entry))), user.csrfToken);
          row.status = "done";
          row.progress = 100;
          setQueue((current) => current.map((entry) => (entry.id === row.id ? { ...entry, status: "done", progress: 100 } : entry)));
        } catch (error) {
          row.status = "failed";
          row.error = error instanceof Error ? error.message : "上传失败";
          setQueue((current) => current.map((entry) => (entry.id === row.id ? { ...entry, status: "failed", error: row.error } : entry)));
        }
      }
    };
    await Promise.all([worker(), worker()]);
    const failed = rows.filter((row) => row.status === "failed").length;
    setMessage(failed ? `${failed} 个素材上传失败，可仅重试失败项` : `已完成 ${rows.length} 个素材上传`);
    await load();
  }
  async function saveAsset(input: Json) {
    try {
      const result = await api<{ message?: string }>(`/catalog/assets/${input.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(result.message || "素材信息已保存");
      setEditing(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "素材保存失败");
    }
  }
  async function registerExternalAsset(input: Json) {
    try {
      const result = await api<{ message?: string }>("/catalog/assets", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(result.message || "外部素材已登记");
      setRegistering(false);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "外部素材登记失败");
    }
  }
  async function removeAsset(asset: Json) {
    try {
      const result = await api<{ message?: string }>(`/catalog/assets/${asset.id}`, { method: "DELETE", headers: { "X-CSRF-Token": user.csrfToken } });
      setMessage(result.message || "素材已删除");
      setPreview(null);
      setEditing(null);
      setDeleteAsset(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "素材删除失败");
    }
  }
  async function decideAsset(asset: Json, decision: "approve" | "return", note: string) {
    setAssetReviewBusy(true);
    try {
      const result = await api<{ message?: string }>(`/reviews/asset/${asset.id}/decision`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify({ decision, note, version: 1 }),
      });
      setMessage(result.message || (decision === "approve" ? "素材已批准" : "素材已退回"));
      setAssetDecision(null);
      setPreview(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "素材审核失败");
    } finally {
      setAssetReviewBusy(false);
    }
  }
  const retryFailed = () => {
    const rows: Json[] = queue
      .filter((row) => row.status === "failed")
      .map((row): Json => ({
        ...row,
        status: "waiting",
        progress: 0,
        error: "",
      }));
    if (!rows.length) return;
    setQueue((current) => current.map((row) => rows.find((retry) => retry.id === row.id) || row));
    runUploadQueue(rows, rows[0]?.metadata || uploadForm).catch((error: Error) => setMessage(error.message));
  };
  return (
    <section className="page">
      <ModuleHeading kicker="资产目录" title="素材中心" copy="沿用 V1 的分类、筛选、预览与单独/批量添加流程；可关联产品、保留来源、添加说明和标签。" badge={loading ? "正在加载" : `${data.assets.length} 条素材`} />
      {message && <p className="message">{message}</p>}
      {loading && <div className="empty loading-state">正在读取素材目录…</div>}
      <div className="asset-toolbar">
        <div className="filter-chips">
          {ASSET_CATEGORIES.map(([key, label]) => (
            <button key={key} disabled={loading} className={filter === key ? "active" : ""} onClick={() => setFilter(key)}>
              {label} {loading ? "—" : key === "all" ? data.assets.length : data.assets.filter((asset) => asset.media_type === key || asset.purpose === key).length}
            </button>
          ))}
        </div>
        {user.permissions.includes("asset.edit") && (
          <div className="inline-actions">
            <button className="secondary" disabled={loading} onClick={() => setRegistering(true)}>
              单独添加
            </button>
            <button className="primary" disabled={loading} onClick={() => setUploading(true)}>
              批量添加
            </button>
          </div>
        )}
      </div>
      <div className="catalog-search">
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索名称、标签、来源、说明或关联产品" />
        <select value={market} onChange={(event) => setMarket(event.target.value)}>
          <option value="all">全部市场</option>
          {markets.map((value) => (
            <option value={value} key={value}>
              {value}
            </option>
          ))}
        </select>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="all">全部状态</option>
          <option value="review">待审核</option>
          <option value="draft">草稿</option>
          <option value="active">已批准 / 可用</option>
          <option value="archived">已归档</option>
        </select>
      </div>
      {queue.length > 0 && (
        <div className="upload-queue">
          <div className="card-row">
            <h3>上传队列（最多 20 个，2 个并发）</h3>
            {queue.some((row) => row.status === "failed") && (
              <button className="secondary" onClick={retryFailed}>
                仅重试失败项
              </button>
            )}
          </div>
          {queue.map((row) => (
            <div className="upload-row" key={row.id}>
              <div>
                <strong>{row.name}</strong>
                <small>
                  {formatBytes(row.size)} · {row.status === "failed" ? row.error : row.status === "done" ? "已完成" : row.status === "uploading" ? "上传中" : "等待上传"}
                </small>
              </div>
              <div className="upload-progress">
                <i style={{ width: `${row.progress || 0}%` }} />
              </div>
              <span>{row.progress || 0}%</span>
            </div>
          ))}
        </div>
      )}
      {!loading && (
        <>
          <div className="asset-grid management-grid">
            {visible.map((asset) => (
              <article className="asset-card" id={`asset-${asset.id}`} key={asset.id}>
                {asset.media_type === "image" && asset.preview_url ? (
                  <button className="asset-thumb" onClick={() => setPreview(asset)}>
                    <img src={asset.preview_url} alt={asset.title} />
                  </button>
                ) : (
                  <button className="asset-kind" onClick={() => setPreview(asset)}>
                    {asset.media_type}
                  </button>
                )}
                <span>{asset.purpose}</span>
                <strong>{asset.title}</strong>
                <small>
                  {asset.market_code || "通用"} · {asset.source_label || "来源未记录"}
                </small>
                <small>{asset.product_code ? `关联：${asset.product_code}` : "未关联产品"}</small>
                <small>{asset.description || "未填写内容说明"}</small>
                {Array.isArray(asset.tags) && asset.tags.length > 0 && (
                  <div className="tag-list">
                    {asset.tags.map((tag: string) => (
                      <i key={tag}>{tag}</i>
                    ))}
                  </div>
                )}
                <div className="card-row">
                  <Status value={asset.status} />
                  <div className="inline-actions">
                    <button className="text-button" onClick={() => setPreview(asset)}>
                      预览
                    </button>
                    {user.permissions.includes("asset.edit") && (
                      <button className="text-button" onClick={() => setEditing(asset)}>
                        编辑
                      </button>
                    )}
                    {user.permissions.includes("asset.edit") && (
                      <button className="danger-button" onClick={() => setDeleteAsset(asset)}>
                        删除
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
          {visible.length === 0 && <div className="empty">暂无符合条件的素材</div>}
          {data.staged.length > 0 && <p className="field-note">另有 {data.staged.length} 个历史素材等待人工校验，未自动加入可用素材库。</p>}
        </>
      )}
      {registering && <AssetExternalWindow products={products} onClose={() => setRegistering(false)} onSave={registerExternalAsset} />}
      {uploading && <AssetUploadWindow form={uploadForm} products={products} inputRef={input} onChange={changeUpload} onClose={() => setUploading(false)} onSelect={(files) => uploadFiles(files)} />}
      {editing && <AssetEditWindow asset={editing} products={products} onClose={() => setEditing(null)} onSave={saveAsset} onDelete={() => setDeleteAsset(editing)} />}
      {preview && (
        <AssetPreviewWindow
          asset={preview}
          onClose={() => setPreview(null)}
          onEdit={() => {
            setPreview(null);
            setEditing(preview);
          }}
          onDelete={() => setDeleteAsset(preview)}
          onReview={(decision) => setAssetDecision({ asset: preview, decision })}
          editable={user.permissions.includes("asset.edit")}
        />
      )}
      {assetDecision && <AssetReviewWindow asset={assetDecision.asset} decision={assetDecision.decision} busy={assetReviewBusy} onClose={() => setAssetDecision(null)} onSubmit={(note) => decideAsset(assetDecision.asset, assetDecision.decision, note)} />}
      {deleteAsset && <ConfirmWindow title="删除素材" description={`确认删除素材“${deleteAsset.title}”？将删除目录记录和本地文件。`} busy={false} onClose={() => setDeleteAsset(null)} onConfirm={() => removeAsset(deleteAsset)} />}
    </section>
  );
}

function AssetUploadWindow({ form, products, inputRef, onChange, onClose, onSelect }: { form: Json; products: Json[]; inputRef: React.RefObject<HTMLInputElement | null>; onChange: (key: string, value: unknown) => void; onClose: () => void; onSelect: (files: File[]) => void }) {
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          inputRef.current?.click();
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">素材入库 · {WORKSPACE.marketCode}</span>
            <h3>批量添加素材</h3>
            <p className="field-note">统一填写一次资料，最多选择 20 个文件；文件媒体类型由服务器校验识别。</p>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label>
            素材类型
            <select value={form.purpose} onChange={(event) => onChange("purpose", event.target.value)}>
              {ASSET_PURPOSES.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            关联产品
            <select value={form.productId} onChange={(event) => onChange("productId", event.target.value)}>
              <option value="">不关联产品</option>
              {products.map((product) => (
                <option value={product.id} key={product.id}>
                  {product.product_code} · {product.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            目标市场
            <input value={WORKSPACE.marketCode} readOnly />
          </label>
          <label className="wide">
            内容说明
            <textarea value={form.description} onChange={(event) => onChange("description", event.target.value)} placeholder="说明素材的场景、可用渠道或注意事项" />
          </label>
          <label className="wide">
            标签（逗号或换行分隔）
            <input value={form.tags} onChange={(event) => onChange("tags", event.target.value)} placeholder={WORKSPACE.code === "DE" ? "例如：W9S, Alltag, Deutschland" : "例如：E8, ECG, 生活场景"} />
          </label>
        </div>
        <input
          ref={inputRef}
          type="file"
          hidden
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,audio/mpeg,audio/wav,application/pdf"
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            event.target.value = "";
            onSelect(files);
          }}
        />
        <div className="actions">
          <button className="primary" type="submit">
            选择文件并上传
          </button>
          <button className="secondary" type="button" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function AssetExternalWindow({ products, onClose, onSave }: { products: Json[]; onClose: () => void; onSave: (input: Json) => void }) {
  const [form, setForm] = useState<Json>({
    title: "",
    sourceUrl: "",
    sourceLabel: "",
    mediaType: "image",
    purpose: "general",
    marketCode: WORKSPACE.marketCode,
    productId: "",
    description: "",
    tags: "",
  });
  const change = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">外部素材登记</span>
            <h3>单独添加素材</h3>
            <p className="field-note">仅登记可访问的外部链接；会保留来源并先进入待审核状态。</p>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label>
            素材名称
            <input required value={form.title} onChange={(event) => change("title", event.target.value)} placeholder="例如 E8 产品场景图" />
          </label>
          <label>
            文件类型
            <select value={form.mediaType} onChange={(event) => change("mediaType", event.target.value)}>
              <option value="image">图片</option>
              <option value="video">视频</option>
              <option value="audio">音频</option>
              <option value="document">文档</option>
            </select>
          </label>
          <label>
            素材用途
            <select value={form.purpose} onChange={(event) => change("purpose", event.target.value)}>
              {ASSET_PURPOSES.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="wide">
            素材地址
            <input required type="url" value={form.sourceUrl} onChange={(event) => change("sourceUrl", event.target.value)} placeholder="https://..." />
          </label>
          <label>
            来源名称
            <input value={form.sourceLabel} onChange={(event) => change("sourceLabel", event.target.value)} placeholder="例如品牌云盘、摄影服务商" />
          </label>
          <label>
            关联产品
            <select value={form.productId} onChange={(event) => change("productId", event.target.value)}>
              <option value="">不关联产品</option>
              {products.map((product) => (
                <option value={product.id} key={product.id}>
                  {product.product_code} · {product.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            目标市场
            <select value={form.marketCode} onChange={(event) => change("marketCode", event.target.value)}>
              <option value="US">US</option>
              <option value="">不指定</option>
            </select>
          </label>
          <label className="wide">
            内容说明
            <textarea value={form.description} onChange={(event) => change("description", event.target.value)} placeholder="说明素材的场景、授权边界或可用渠道" />
          </label>
          <label className="wide">
            标签（逗号或换行分隔）
            <input value={form.tags} onChange={(event) => change("tags", event.target.value)} placeholder="例如：E8, ECG, 生活场景" />
          </label>
        </div>
        <div className="actions">
          <button className="primary">登记素材</button>
          <button className="secondary" type="button" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function AssetEditWindow({ asset, products, onClose, onSave, onDelete }: { asset: Json; products: Json[]; onClose: () => void; onSave: (input: Json) => void; onDelete: () => void }) {
  const [form, setForm] = useState<Json>(() => ({
    id: asset.id,
    title: asset.title || "",
    purpose: asset.purpose || "general",
    marketCode: asset.market_code || "",
    productId: asset.product_id || "",
    description: asset.description || "",
    tags: Array.isArray(asset.tags) ? asset.tags.join(", ") : asset.tags || "",
  }));
  const change = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">素材信息</span>
            <h3>编辑素材</h3>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label className="wide">
            素材名称
            <input required value={form.title} onChange={(event) => change("title", event.target.value)} />
          </label>
          <label>
            素材类型
            <select value={form.purpose} onChange={(event) => change("purpose", event.target.value)}>
              {ASSET_PURPOSES.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            关联产品
            <select value={form.productId} onChange={(event) => change("productId", event.target.value)}>
              <option value="">不关联产品</option>
              {products.map((product) => (
                <option value={product.id} key={product.id}>
                  {product.product_code} · {product.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            目标市场
            <select value={form.marketCode} onChange={(event) => change("marketCode", event.target.value)}>
              <option value="US">US</option>
              <option value="">不指定</option>
            </select>
          </label>
          <label className="wide">
            内容说明
            <textarea value={form.description} onChange={(event) => change("description", event.target.value)} />
          </label>
          <label className="wide">
            标签（逗号或换行分隔）
            <input value={form.tags} onChange={(event) => change("tags", event.target.value)} />
          </label>
        </div>
        <div className="actions">
          <button className="primary">保存信息</button>
          <button type="button" className="danger-button" onClick={onDelete}>
            删除素材
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function AssetPreviewWindow({ asset, onClose, onEdit, onDelete, onReview, editable }: { asset: Json; onClose: () => void; onEdit: () => void; onDelete: () => void; onReview: (decision: "approve" | "return") => void; editable: boolean }) {
  const url = asset.preview_url;
  const media = asset.media_type === "image" ? <img src={url} alt={asset.title} /> : asset.media_type === "video" ? <video src={url} controls preload="metadata" /> : asset.media_type === "audio" ? <audio src={url} controls /> : asset.mime_type === "application/pdf" ? <iframe src={url} title={asset.title} /> : null;
  const reviewable = editable && asset.status === "review";
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <section className="preview-dialog">
        <div className="card-row">
          <div>
            <span className="eyebrow">素材预览</span>
            <h3>{asset.title}</h3>
          </div>
          <button className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        {media || <p>此文件不支持内嵌预览。</p>}
        <div className="preview-metadata">
          <p>
            <strong>类型：</strong>
            {asset.media_type} · {asset.purpose}
          </p>
          <p>
            <strong>来源：</strong>
            {asset.source_label || "未记录"}
          </p>
          <p>
            <strong>关联产品：</strong>
            {asset.product_code || "未关联"}
          </p>
          <p>
            <strong>内容说明：</strong>
            {asset.description || "未填写"}
          </p>
          <p>
            <strong>标签：</strong>
            {Array.isArray(asset.tags) && asset.tags.length ? asset.tags.join("、") : "未设置"}
          </p>
          <p>
            <strong>文件：</strong>
            {asset.storage_driver === "external-reference" ? "外部链接" : `${asset.mime_type} · ${formatBytes(asset.size_bytes)}`}
          </p>
        </div>
        <div className="actions">
          {url && (
            <a className="primary" href={url} target="_blank" rel="noreferrer">
              打开或下载
            </a>
          )}
          {reviewable && (
            <button className="primary" onClick={() => onReview("approve")}>
              审核并批准
            </button>
          )}
          {reviewable && (
            <button className="secondary" onClick={() => onReview("return")}>
              审核退回
            </button>
          )}
          {editable && (
            <button className="secondary" onClick={onEdit}>
              编辑
            </button>
          )}
          {editable && (
            <button className="danger-button" onClick={onDelete}>
              删除
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function AssetReviewWindow({ asset, decision, busy, onClose, onSubmit }: { asset: Json; decision: "approve" | "return"; busy: boolean; onClose: () => void; onSubmit: (note: string) => void }) {
  const returning = decision === "return";
  const [note, setNote] = useState(returning ? "" : "已查看素材内容、来源与关联产品信息，批准当前版本可用。");
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true" aria-labelledby="asset-review-title">
      <form
        className="preview-dialog editor-window decision-window"
        onSubmit={(event) => {
          event.preventDefault();
          if (returning && !note.trim()) return;
          if (!returning && !confirmed) return;
          onSubmit(note.trim());
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">素材人工审核</span>
            <h3 id="asset-review-title">{returning ? "退回素材" : "批准素材"}</h3>
          </div>
          <button type="button" className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="review-decision-summary">
          <strong>{asset.title}</strong>
          <span>
            {asset.media_type} · {asset.purpose} · {asset.product_code || "未关联产品"}
          </span>
          <small>来源：{asset.source_label || "未记录"}</small>
        </div>
        {returning ? (
          <label className="wide">
            退回原因
            <textarea required autoFocus rows={5} value={note} onChange={(event) => setNote(event.target.value)} placeholder="说明需要补充、修改或核验的内容" />
          </label>
        ) : (
          <>
            <p className="field-note">批准后，该素材将标记为可用；请确认已预览内容并核对来源和使用边界。</p>
            <label className="checkbox-line">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
              我已查看素材内容、来源与使用边界，确认批准。
            </label>
          </>
        )}
        <div className="actions">
          <button className={returning ? "danger-button" : "primary"} disabled={busy || (!returning && !confirmed)}>
            {busy ? "正在提交…" : returning ? "确认退回" : "确认批准"}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function uploadAssetFile(file: File, metadata: Json, onProgress: (value: number) => void, csrfToken: string) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("file", file);
    ["purpose", "marketCode", "productId", "description", "tags"].forEach((key) => {
      const value = metadata[key];
      if (value !== undefined && value !== null) form.append(key, String(value));
    });
    xhr.open("POST", "/api/v2/catalog/assets/upload");
    xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      let payload: Json = {};
      try {
        payload = JSON.parse(xhr.responseText || "{}");
      } catch {
        payload = {};
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload.ok !== false) resolve();
      else reject(new Error(payload.message || payload.error || `上传失败 (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("网络中断"));
    xhr.send(form);
  });
}

function Knowledge({ user, focusTarget }: { user: User; focusTarget?: FocusTarget | null }) {
  const [rows, setRows] = useState<Json[]>([]);
  const [editing, setEditing] = useState<Json | null>(null);
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<Json | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteKnowledge, setDeleteKnowledge] = useState<Json | null>(null);
  const load = async () => {
    setLoading(true);
    try {
      const result = await api<{ knowledge: Json[] }>("/catalog/knowledge");
      setRows(result.knowledge);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load().catch((error: Error) => setMessage(error.message));
  }, []);
  async function open(id: string, mode: "edit" | "preview") {
    try {
      const result = await api<{ knowledge: Json }>(`/catalog/knowledge/${id}`);
      if (mode === "edit") {
        setEditing(result.knowledge);
        setCreating(false);
      } else setPreview(result.knowledge);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法读取知识内容");
    }
  }
  useEffect(() => {
    if (!focusTarget || !rows.length) return;
    const item = rows.find((row) => String(row.id) === focusTarget.id);
    if (!item) {
      setMessage("未找到需要审核的知识记录，可能已被删除或归档。");
      return;
    }
    document.getElementById(`knowledge-${item.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    open(String(item.id), focusTarget.mode);
  }, [focusTarget, rows]);
  async function save(input: Json) {
    try {
      const result = await api<{ knowledge: Json; message?: string }>(input.id ? `/catalog/knowledge/${input.id}` : "/catalog/knowledge", {
        method: input.id ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      setMessage(result.message || "知识内容已保存");
      setCreating(false);
      setEditing(result.knowledge);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "知识内容保存失败");
    }
  }
  async function remove(item: Json) {
    try {
      const result = await api<{ message?: string }>(`/catalog/knowledge/${item.id}`, { method: "DELETE", headers: { "X-CSRF-Token": user.csrfToken } });
      setMessage(result.message || "知识内容已删除");
      setEditing(null);
      setPreview(null);
      setDeleteKnowledge(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "知识内容删除失败");
    }
  }
  return (
    <section className="page">
      <ModuleHeading kicker="证据与知识" title="知识中心" copy="知识内容在保存后自动切分为受控检索片段；可编辑、预览或删除，供本地 Codex 在授权任务中引用。" badge={loading ? "正在加载" : `${rows.length} 条知识`} />
      {message && <p className="message">{message}</p>}
      {loading && <div className="empty loading-state">正在读取知识内容…</div>}
      <div className="asset-toolbar">
        <p className="field-note">知识与前台内容分开管理，避免未审核资料被直接发布。</p>
        {user.permissions.includes("knowledge.edit") && (
          <button
            className="primary"
            disabled={loading}
            onClick={() => {
              setCreating(true);
              setEditing(null);
            }}
          >
            新建知识
          </button>
        )}
      </div>
      {!loading && (
        <>
          <KnowledgeSearchPanel onOpen={(id) => open(id, "preview")} />
          <div className="knowledge-grid">
            {rows.map((item) => (
              <article className="record-card" id={`knowledge-${item.id}`} key={item.id}>
                <div className="card-row">
                  <span className="eyebrow">{item.document_type}</span>
                  <Status value={item.status} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.excerpt || "暂无摘要"}</p>
                <small>
                  {item.source_label || "未填写来源"} · {item.market_code || "通用"}
                </small>
                <div className="actions">
                  <button className="secondary" onClick={() => open(item.id, "preview")}>
                    预览
                  </button>
                  {user.permissions.includes("knowledge.edit") && (
                    <button className="secondary" onClick={() => open(item.id, "edit")}>
                      编辑
                    </button>
                  )}
                  {user.permissions.includes("knowledge.edit") && (
                    <button className="danger-button" onClick={() => setDeleteKnowledge(item)}>
                      删除
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
          {rows.length === 0 && <div className="empty">暂无知识内容</div>}
        </>
      )}
      {creating && <KnowledgeEditorWindow item={{}} creating onClose={() => setCreating(false)} onSave={save} />}
      {editing && <KnowledgeEditorWindow item={editing} creating={false} onClose={() => setEditing(null)} onSave={save} onDelete={() => setDeleteKnowledge(editing)} />}
      {preview && (
        <KnowledgePreviewWindow
          item={preview}
          onClose={() => setPreview(null)}
          onEdit={() => {
            setPreview(null);
            setEditing(preview);
          }}
          editable={user.permissions.includes("knowledge.edit")}
        />
      )}
      {deleteKnowledge && <ConfirmWindow title="删除知识内容" description={`确认删除知识内容“${deleteKnowledge.title}”？删除后将无法再被任务引用。`} busy={false} onClose={() => setDeleteKnowledge(null)} onConfirm={() => remove(deleteKnowledge)} />}
    </section>
  );
}

function KnowledgeEditorWindow({ item, creating, onClose, onSave, onDelete }: { item: Json; creating: boolean; onClose: () => void; onSave: (input: Json) => void; onDelete?: () => void }) {
  const [form, setForm] = useState<Json>(() => ({
    id: item.id,
    documentType: item.document_type || "product_fact",
    title: item.title || "",
    content: item.content || "",
    sourceUrl: item.source_url || "",
    sourceLabel: item.source_label || "",
    marketCode: WORKSPACE.marketCode,
    status: item.status || "review",
    validFrom: item.valid_from || "",
    validUntil: item.valid_until || "",
  }));
  const change = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">受控知识</span>
            <h3>{creating ? "新建知识内容" : "编辑知识内容"}</h3>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label>
            知识类型
            <select value={form.documentType} onChange={(event) => change("documentType", event.target.value)}>
              <option value="product_fact">产品事实</option>
              <option value="faq">常见问题</option>
              <option value="evidence">证据资料</option>
              <option value="policy">政策规范</option>
              <option value="sop">操作 SOP</option>
              <option value="other">其他</option>
            </select>
          </label>
          <label>
            状态
            <select value={form.status} onChange={(event) => change("status", event.target.value)}>
              <option value="draft">草稿</option>
              <option value="review">待审核</option>
              <option value="published">已生效</option>
              <option value="archived">已归档</option>
            </select>
          </label>
          <label>
            市场
            <input value={WORKSPACE.marketCode} readOnly />
          </label>
          <label className="wide">
            知识标题
            <input required value={form.title} onChange={(event) => change("title", event.target.value)} />
          </label>
          <label className="wide">
            正文
            <textarea required rows={15} value={form.content} onChange={(event) => change("content", event.target.value)} placeholder="只填写可核实、可追溯的知识内容。" />
          </label>
          <label>
            来源名称
            <input value={form.sourceLabel} onChange={(event) => change("sourceLabel", event.target.value)} />
          </label>
          <label>
            来源地址
            <input type="url" value={form.sourceUrl} onChange={(event) => change("sourceUrl", event.target.value)} />
          </label>
          <label>
            生效日期
            <input type="date" value={form.validFrom} onChange={(event) => change("validFrom", event.target.value)} />
          </label>
          <label>
            失效日期
            <input type="date" value={form.validUntil} onChange={(event) => change("validUntil", event.target.value)} />
          </label>
        </div>
        <div className="actions">
          <button className="primary">保存知识</button>
          {onDelete && (
            <button type="button" className="danger-button" onClick={onDelete}>
              删除
            </button>
          )}
          <button type="button" className="secondary" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function KnowledgePreviewWindow({ item, onClose, onEdit, editable }: { item: Json; onClose: () => void; onEdit: () => void; editable: boolean }) {
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <section className="preview-dialog knowledge-preview">
        <div className="card-row">
          <div>
            <span className="eyebrow">知识预览 · {item.document_type}</span>
            <h3>{item.title}</h3>
          </div>
          <button className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="preview-metadata">
          <span>{item.status}</span>
          <span>{item.source_label || "未填写来源"}</span>
          {item.source_url && (
            <a href={item.source_url} target="_blank" rel="noreferrer">
              打开来源
            </a>
          )}
        </div>
        <article>{item.content}</article>
        <div className="actions">
          {editable && (
            <button className="secondary" onClick={onEdit}>
              编辑
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function ConfirmWindow({ title, description, busy, onClose, onConfirm }: { title: string; description: string; busy: boolean; onClose: () => void; onConfirm: () => void }) {
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true" aria-labelledby="confirm-window-title">
      <section className="preview-dialog editor-window decision-window">
        <div className="card-row">
          <div>
            <span className="eyebrow">高风险操作确认</span>
            <h3 id="confirm-window-title">{title}</h3>
          </div>
          <button className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        <p>{description}</p>
        <label className="checkbox-line">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          我已确认这是需要删除的记录。
        </label>
        <div className="actions">
          <button className="danger-button" disabled={busy || !confirmed} onClick={onConfirm}>
            {busy ? "正在删除…" : "确认删除"}
          </button>
          <button className="secondary" onClick={onClose}>
            取消
          </button>
        </div>
      </section>
    </div>
  );
}

function Content({ user, focusContentId, focusMode = "preview" }: { user: User; focusContentId: string | null; focusMode?: "preview" | "edit" }) {
  const [rows, setRows] = useState<Json[]>([]);
  const [products, setProducts] = useState<Json[]>([]);
  const [assets, setAssets] = useState<Json[]>([]);
  const [editing, setEditing] = useState<Json | null>(null);
  const [preview, setPreview] = useState<Json | null>(null);
  const [createMode, setCreateMode] = useState<"content" | "video" | null>(null);
  const [filter, setFilter] = useState("all");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const [content, productData, assetData] = await Promise.all([api<{ content: Json[] }>("/catalog/content"), api<{ products: Json[] }>("/catalog/products"), api<{ assets: Json[] }>("/catalog/assets")]);
      setRows(content.content);
      setProducts(productData.products);
      setAssets(assetData.assets.filter((asset) => ["active", "approved"].includes(String(asset.status)) && asset.rights_status === "approved" && asset.storage_driver === "v2-local" && ["video", "image", "audio"].includes(String(asset.media_type))));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load().catch((error: Error) => setMessage(error.message));
  }, []);
  useEffect(() => {
    if (focusContentId) {
      document.getElementById(`content-${focusContentId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      open(focusContentId, focusMode);
    }
  }, [focusContentId, focusMode]);
  async function open(id: string, mode: "edit" | "preview") {
    try {
      const result = await api<{ content: Json }>(`/catalog/content/${id}`);
      if (mode === "edit") {
        setEditing(result.content);
        setCreateMode(null);
      } else {
        setReviewError("");
        setPreview(result.content);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "无法读取内容");
    }
  }
  async function action(id: string, path: string, body: Json = {}) {
    try {
      const result = await api<{ message?: string }>(`/catalog/content/${id}/${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(body),
      });
      setMessage(result.message || "操作完成");
      await load();
      if (editing?.id === id) await open(id, "edit");
      if (preview?.id === id) await open(id, "preview");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "操作失败");
    }
  }
  async function decideFromPreview(id: string, decision: "approve" | "return", note: string) {
    setReviewBusy(true);
    setReviewError("");
    try {
      const result = await api<{
        message?: string;
        review?: { auto_publish_status?: string };
      }>(`/catalog/reviews/${id}/decision`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify({ decision, note }),
      });
      const resultMessage = result.message || (decision === "approve" ? "当前版本已批准并进入自动发布" : "当前版本已退回修改");
      setMessage(resultMessage);
      await load();
      if (decision === "approve") {
        setPreview(null);
      } else {
        await open(id, "preview");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "审核操作失败";
      setReviewError(errorMessage);
      setMessage(errorMessage);
    } finally {
      setReviewBusy(false);
    }
  }
  async function save(input: Json): Promise<{ ok: boolean; message: string; content?: Json }> {
    try {
      const result = await api<{ content: Json; message?: string }>(input.id ? `/catalog/content/${input.id}` : "/catalog/content", {
        method: input.id ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": user.csrfToken,
        },
        body: JSON.stringify(input),
      });
      const successMessage = result.message || (input.contentKind === "video" ? "短视频任务已创建并提交本地 Codex 队列" : "内容草稿已创建");
      setMessage(successMessage);
      setCreateMode(null);
      setEditing(result.content);
      await load();
      return { ok: true, message: successMessage, content: result.content };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "内容保存失败";
      setMessage(errorMessage);
      return { ok: false, message: errorMessage };
    }
  }
  const filters: Array<[string, string]> = [
    ["all", "全部"],
    ["video", "短视频"],
    ["article", "博客"],
    ["page", "Shopify 页"],
  ];
  const visible = rows.filter((row) => filter === "all" || row.content_kind === filter);
  return (
    <section className="page">
      <ModuleHeading kicker="内容版本与渠道" title="内容中心" copy="新建内容、短视频、编辑与预览分别在独立窗口完成；审核通过的博客和 Shopify 页面会自动发布，失败时保留批准状态并可手工重试。" badge={loading ? "正在加载" : `${rows.length} 条内容`} />
      {message && <p className="message">{message}</p>}
      {loading && <div className="empty loading-state">正在读取内容与产品关联…</div>}
      <div className="asset-toolbar">
        <div className="filter-chips">
          {filters.map(([key, label]) => (
            <button disabled={loading} className={filter === key ? "active" : ""} key={key} onClick={() => setFilter(key)}>
              {label} {loading ? "—" : key === "all" ? rows.length : rows.filter((row) => row.content_kind === key).length}
            </button>
          ))}
        </div>
        {user.permissions.includes("content.edit") && (
          <div className="actions">
            <button
              className="secondary"
              disabled={loading}
              onClick={() => {
                setEditing(null);
                setCreateMode("content");
              }}
            >
              新建内容
            </button>
            <button
              className="primary"
              disabled={loading}
              onClick={() => {
                setEditing(null);
                setCreateMode("video");
              }}
            >
              新建视频
            </button>
          </div>
        )}
      </div>
      {!loading && (
        <>
          <div className="list-grid">
            {visible.map((row) => (
              <article className="record-card" id={`content-${row.id}`} key={row.id}>
                <div className="card-row">
                  <span className="eyebrow">
                    {row.content_kind === "page" ? "Shopify 页面" : row.content_kind === "video" ? "短视频" : "博客文章"} · {row.market_code || "US"}/{row.locale_code || "en-US"} · {row.product_code || "未关联产品"}
                  </span>
                  <Status value={row.status} />
                </div>
                <h3>{row.title}</h3>
                <p>
                  版本 v{row.current_version} · 风险 {row.risk_status}
                </p>
                {row.content_kind === "video" ? (
                  <p className="field-note">
                    本地 Codex：{row.video_task_status || "尚未提交"}
                    {row.video_task_error ? ` · ${row.video_task_error}` : ""}
                  </p>
                ) : row.shopify_url ? (
                  <a className="live-link" href={row.shopify_url} target="_blank" rel="noreferrer">
                    已上线 · 打开前台
                  </a>
                ) : (
                  <p className={row.last_error ? "error" : "field-note"}>{row.last_error ? `自动发布失败：${row.last_error}` : "审核通过后将自动发布到 Shopify"}</p>
                )}
                <div className="actions">
                  <button className="secondary" onClick={() => open(row.id, "preview")}>
                    预览
                  </button>
                  {user.permissions.includes("content.edit") && (
                    <button className="secondary" onClick={() => open(row.id, "edit")}>
                      编辑
                    </button>
                  )}
                  {user.permissions.includes("content.edit") && row.content_kind === "video" && (
                    <button className="secondary" onClick={() => action(row.id, "generate-video")}>
                      提交本地 Codex
                    </button>
                  )}
                  {user.permissions.includes("content.edit") && row.status === "draft" && (
                    <button className="secondary" onClick={() => action(row.id, "submit-review")}>
                      提交审核
                    </button>
                  )}
                  {user.permissions.includes("content.approve") && row.status === "approved" && row.content_kind !== "video" && (
                    <button className="primary" onClick={() => action(row.id, "publish")}>
                      {row.last_error ? "重试发布到 Shopify" : "发布到 Shopify"}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
          {visible.length === 0 && <div className="empty">该分类暂无内容</div>}
          <ContentWorkflowPanel user={user} contents={rows} onRefresh={load} />
        </>
      )}
      {createMode === "content" && <ContentCreateWindow products={products} onClose={() => setCreateMode(null)} onSave={save} />}
      {createMode === "video" && <VideoCreateWindow products={products} assets={assets} onClose={() => setCreateMode(null)} onSave={save} />}
      {editing && <ContentEditor key={editing.id} item={editing} creating={false} products={products} assets={assets} onClose={() => setEditing(null)} onSave={save} onSubmitReview={(id) => action(id, "submit-review")} onPublish={(id) => action(id, "publish")} />}
      {preview && (
        <ContentPreviewWindow
          item={preview}
          busy={reviewBusy}
          errorMessage={reviewError}
          canApprove={user.permissions.includes("content.approve")}
          onDecision={(decision, note) => decideFromPreview(preview.id, decision, note)}
          onClose={() => setPreview(null)}
          onEdit={() => {
            setPreview(null);
            setEditing(preview);
          }}
        />
      )}
    </section>
  );
}

function ContentCreateWindow({ products, onClose, onSave }: { products: Json[]; onClose: () => void; onSave: (input: Json) => void }) {
  const outputLanguage = WORKSPACE.contentLanguageLabel;
  const [form, setForm] = useState<Json>({
    contentKind: "article",
    title: "",
    handle: "",
    category: WORKSPACE.defaultContentCategory,
    productId: "",
    blogHandle: "health-center",
    targetKeyword: "",
    summaryHtml: "",
    riskStatus: "review",
    bodyHtml: "",
  });
  const change = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window create-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">内容任务</span>
            <h3>新建内容</h3>
            <p className="field-note">先创建简明任务；正文、场景图与 SEO 在独立编辑窗口继续完善。</p>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label>
            内容形式
            <select value={form.contentKind} onChange={(event) => change("contentKind", event.target.value)}>
              <option value="article">博客文章</option>
              <option value="page">Shopify 页面</option>
            </select>
          </label>
          <label>
            关联产品
            <select value={form.productId} onChange={(event) => change("productId", event.target.value)}>
              <option value="">暂不关联</option>
              {products.map((product) => (
                <option value={product.id} key={product.id}>
                  {product.product_code} · {product.name}
                </option>
              ))}
            </select>
          </label>
          <label className="wide">
            {outputLanguage}标题
            <input required value={form.title} onChange={(event) => change("title", event.target.value)} placeholder="要解决的真实用户问题" />
          </label>
          <label>
            {outputLanguage}链接 handle
            <input required value={form.handle} onChange={(event) => change("handle", event.target.value)} placeholder="how-to-..." />
          </label>
          <label>
            内容分类
            <input value={form.category} onChange={(event) => change("category", event.target.value)} />
          </label>
          {form.contentKind === "article" && (
            <label>
              博客栏目
              <input value={form.blogHandle} onChange={(event) => change("blogHandle", event.target.value)} />
            </label>
          )}
          <label>
            目标关键词
            <input value={form.targetKeyword} onChange={(event) => change("targetKeyword", event.target.value)} />
          </label>
          <label>
            风险状态
            <select value={form.riskStatus} onChange={(event) => change("riskStatus", event.target.value)}>
              <option value="review">需要审核</option>
              <option value="low">低风险</option>
              <option value="blocked">阻断</option>
            </select>
          </label>
          <label className="wide">
            内容简报
            <textarea value={form.summaryHtml} onChange={(event) => change("summaryHtml", event.target.value)} placeholder="说明用户问题、回答方向与健康声明边界" />
          </label>
        </div>
        <div className="actions">
          <button className="primary">创建草稿</button>
          <button className="secondary" type="button" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function slugifyHandle(value: unknown) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 150);
}

function VideoCreateWindow({ products, assets, onClose, onSave }: { products: Json[]; assets: Json[]; onClose: () => void; onSave: (input: Json) => Promise<{ ok: boolean; message: string }> }) {
  const [form, setForm] = useState<Json>({
    contentKind: "video",
    title: "",
    handle: "",
    category: "Short Video",
    productId: "",
    referenceVideoUrl: "",
    videoPrompt: "",
    videoDuration: 30,
    aspectRatio: "9:16",
    targetPlatforms: ["tiktok"],
    assetIds: [],
    approvedScriptOnly: false,
    caption: "",
    landingUrl: "",
    riskStatus: "review",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const change = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  const toggle = (platform: string) => change("targetPlatforms", form.targetPlatforms.includes(platform) ? form.targetPlatforms.filter((value: string) => value !== platform) : [...form.targetPlatforms, platform]);
  const toggleAsset = (assetId: string) => change("assetIds", form.assetIds.includes(assetId) ? form.assetIds.filter((value: string) => value !== assetId) : [...form.assetIds, assetId]);
  const selectedProduct = products.find((product) => product.id === form.productId);
  const productCode = String(selectedProduct?.product_code || "").toLowerCase();
  const relevantAssets = assets.filter((asset) => {
    const searchable = `${asset.title || ""} ${(asset.tags || []).join(" ")} ${asset.product_code || ""}`.toLowerCase();
    return !productCode || asset.product_id === form.productId || searchable.includes(productCode);
  });
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const normalizedHandle = slugifyHandle(form.handle);
    if (!normalizedHandle) {
      setError("链接 handle 只能使用英文字母、数字和连字符，例如 w9s-blood-pressure-watch。");
      return;
    }
    if (!form.approvedScriptOnly) {
      setError("请先确认提示词已包含可直接制作的完整脚本，并已完成人工审核。");
      return;
    }
    if (!form.assetIds.length) {
      setError("请至少选择 1 个已批准的本地生产素材；参考视频只用于风格参考，不能代替授权素材。");
      return;
    }
    setBusy(true);
    const result = await onSave({ ...form, handle: normalizedHandle });
    if (!result.ok) setError(result.message);
    setBusy(false);
  }
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form className="preview-dialog editor-window create-window" onSubmit={submit}>
        <div className="card-row">
          <div>
            <span className="eyebrow">本地 Codex 视频任务</span>
            <h3>新建视频</h3>
            <p className="field-note">创建后进入本地 Codex 队列；只使用已批准素材，成片、封面与平台发布仍须人工审核。</p>
          </div>
          <button className="text-button" type="button" disabled={busy} onClick={onClose}>
            关闭
          </button>
        </div>
        <div className="form-grid compact-form">
          <label className="wide">
            视频任务名称
            <input required value={form.title} onChange={(event) => change("title", event.target.value)} />
          </label>
          <label>
            英文链接 handle
            <input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={form.handle} onChange={(event) => change("handle", slugifyHandle(event.target.value))} placeholder="w9s-product-video" />
            <small>空格会自动转成连字符。</small>
          </label>
          <label>
            关联产品
            <select required value={form.productId} onChange={(event) => change("productId", event.target.value)}>
              <option value="">请选择产品</option>
              {products.map((product) => (
                <option value={product.id} key={product.id}>
                  {product.product_code} · {product.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            时长
            <select value={form.videoDuration} onChange={(event) => change("videoDuration", event.target.value)}>
              {[15, 30, 45, 60].map((seconds) => (
                <option key={seconds} value={seconds}>
                  {seconds} 秒
                </option>
              ))}
            </select>
          </label>
          <label>
            画幅
            <select value={form.aspectRatio} onChange={(event) => change("aspectRatio", event.target.value)}>
              <option value="9:16">9:16 竖屏</option>
              <option value="1:1">1:1 方形</option>
              <option value="16:9">16:9 横屏</option>
            </select>
          </label>
          <label className="wide">
            参考视频地址
            <input type="url" value={form.referenceVideoUrl} onChange={(event) => change("referenceVideoUrl", event.target.value)} placeholder="https://..." />
            <small>仅作风格参考，不会把外部视频当作可商用素材。</small>
          </label>
          <label className="wide">
            已审核脚本与制作提示
            <textarea required rows={6} value={form.videoPrompt} onChange={(event) => change("videoPrompt", event.target.value)} placeholder="完整写明旁白/字幕、镜头顺序、已核实卖点、节奏以及不可出现的内容" />
          </label>
          <label className="wide checkbox-line">
            <input type="checkbox" checked={form.approvedScriptOnly} onChange={(event) => change("approvedScriptOnly", event.target.checked)} />
            我确认上方内容已包含可直接制作的完整脚本，并已完成人工审核。
          </label>
          <fieldset className="wide platform-checks">
            <legend>已批准生产素材（至少 1 个）</legend>
            {relevantAssets.length ? (
              relevantAssets.map((asset) => (
                <label key={asset.id}>
                  <input type="checkbox" checked={form.assetIds.includes(asset.id)} onChange={() => toggleAsset(asset.id)} />
                  {asset.media_type} · {asset.title}
                </label>
              ))
            ) : (
              <small>{form.productId ? "该产品暂无已批准、可下载的本地图片/视频/音频，请先到素材中心上传并审核。" : "请先选择关联产品。"}</small>
            )}
          </fieldset>
          <fieldset className="wide platform-checks">
            <legend>审核通过后自动分发到</legend>
            {["facebook", "instagram", "tiktok", "youtube", "pinterest"].map((platform) => (
              <label key={platform}>
                <input type="checkbox" checked={form.targetPlatforms.includes(platform)} onChange={() => toggle(platform)} />
                {platform}
              </label>
            ))}
          </fieldset>
          <label className="wide">
            统一社媒正文
            <textarea value={form.caption} onChange={(event) => change("caption", event.target.value)} />
          </label>
          <label className="wide">
            商品落地页
            <input type="url" value={form.landingUrl} onChange={(event) => change("landingUrl", event.target.value)} />
          </label>
        </div>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="primary" disabled={busy}>
            {busy ? "正在创建并提交…" : "创建并提交队列"}
          </button>
          <button className="secondary" type="button" disabled={busy} onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function ContentEditor({ item, creating, products, assets = [], onClose, onSave, onSubmitReview, onPublish }: { item: Json; creating: boolean; products: Json[]; assets?: Json[]; onClose: () => void; onSave: (input: Json) => void; onSubmitReview: (id: string) => void; onPublish: (id: string) => void }) {
  const body = item.body_json || {};
  const seo = item.seo_json || {};
  const [section, setSection] = useState("basic");
  const [form, setForm] = useState<Json>(() => ({
    title: item.title || "",
    handle: item.handle || "",
    contentKind: item.content_kind || "article",
    category: item.category || "General",
    productId: item.primary_product_id || "",
    summaryHtml: body.summaryHtml || "",
    bodyHtml: body.bodyHtml || "",
    sceneImageUrls: Array.isArray(body.sceneImageUrls) ? body.sceneImageUrls.join("\n") : "",
    seoTitle: seo.title || item.title || "",
    seoDescription: seo.description || "",
    targetKeyword: seo.targetKeyword || "",
    riskStatus: item.risk_status || "review",
    blogHandle: item.shopify_blog_handle || "health-center",
    translationSourceResourceId: item.translation_source_resource_id || "",
    referenceVideoUrl: body.referenceVideoUrl || "",
    videoPrompt: body.videoPrompt || "",
    assetIds: Array.isArray(body.assetIds) ? body.assetIds : [],
    approvedScriptOnly: body.approvedScriptOnly === true,
    targetPlatforms: Array.isArray(body.targetPlatforms) ? body.targetPlatforms : Array.isArray(body.distributionPlatforms) ? body.distributionPlatforms : body.targetPlatform ? [body.targetPlatform] : [],
    videoDuration: body.videoDuration || body.durationSeconds || 30,
    aspectRatio: body.aspectRatio || "9:16",
    videoOutputUrl: body.videoOutputUrl || "",
    coverUrl: body.coverUrl || body.videoCoverUrl || "",
    landingUrl: body.landingUrl || "",
    disclosure: editorDisclosure(body.disclosure) || "AI-assisted product video. Review product specifications before ordering.",
    caption: body.caption || body.socialCaption || "",
    author: body.author || "",
    tags: Array.isArray(body.tags) ? body.tags.join(", ") : body.tags || "",
    evidence: typeof body.evidence === "string" ? body.evidence : JSON.stringify(body.evidence || [], null, 2),
    riskReasons: Array.isArray(body.riskReasons) ? body.riskReasons.join("\n") : body.riskReasons || "",
    internalLinks: typeof body.internalLinks === "string" ? body.internalLinks : JSON.stringify(body.internalLinks || [], null, 2),
  }));
  const change = (key: string, value: unknown) => setForm((current) => ({ ...current, [key]: value }));
  const togglePlatform = (platform: string) => change("targetPlatforms", form.targetPlatforms.includes(platform) ? form.targetPlatforms.filter((value: string) => value !== platform) : [...form.targetPlatforms, platform]);
  const toggleAsset = (assetId: string) => change("assetIds", form.assetIds.includes(assetId) ? form.assetIds.filter((value: string) => value !== assetId) : [...form.assetIds, assetId]);
  const selectedProduct = products.find((product) => product.id === form.productId);
  const productCode = String(selectedProduct?.product_code || "").toLowerCase();
  const relevantAssets = assets.filter((asset) => {
    const searchable = `${asset.title || ""} ${(asset.tags || []).join(" ")} ${asset.product_code || ""}`.toLowerCase();
    return !productCode || asset.product_id === form.productId || searchable.includes(productCode);
  });
  const isVideo = form.contentKind === "video";
  const tabs = isVideo
    ? [
        ["basic", "基本信息"],
        ["production", "生成设置"],
        ["distribution", "发布资料"],
      ]
    : [
        ["basic", "基本信息"],
        ["copy", "正文与场景图"],
        ["seo", "SEO 与合规"],
      ];
  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <form
        className="preview-dialog editor-window content-editor-window"
        onSubmit={(event) => {
          event.preventDefault();
          onSave({ ...(creating ? {} : { id: item.id }), ...form });
        }}
      >
        <div className="card-row">
          <div>
            <span className="eyebrow">内容编辑</span>
            <h3>{creating ? "新建内容" : `编辑内容 · v${item.current_version}`}</h3>
          </div>
          <button className="text-button" type="button" onClick={onClose}>
            关闭
          </button>
        </div>
        {!creating && (
          <details className="content-history">
            <summary>版本与审核记录</summary>
            <p>
              历史版本：
              {Array.isArray(item.versions) ? item.versions.map((version: Json) => `v${version.version}`).join("、") : "暂无"}
            </p>
            {Array.isArray(item.reviews) && item.reviews.length > 0 && (
              <ul>
                {item.reviews.slice(0, 5).map((review: Json, index: number) => (
                  <li key={`${review.created_at || index}`}>
                    {review.decision === "approve" ? "批准" : "退回"} · {review.note || "无说明"}
                  </li>
                ))}
              </ul>
            )}
          </details>
        )}
        <nav className="editor-tabs">
          {tabs.map(([key, label]) => (
            <button type="button" key={key} className={section === key ? "active" : ""} onClick={() => setSection(String(key))}>
              {label}
            </button>
          ))}
        </nav>
        <div className="form-grid compact-form">
          {section === "basic" && (
            <>
              <label>
                内容类型
                <select
                  value={form.contentKind}
                  onChange={(event) => {
                    change("contentKind", event.target.value);
                    setSection("basic");
                  }}
                >
                  <option value="article">博客文章</option>
                  <option value="page">Shopify 页面</option>
                  <option value="video">短视频</option>
                </select>
              </label>
              <label>
                关联产品
                <select value={form.productId} onChange={(event) => change("productId", event.target.value)}>
                  <option value="">未关联</option>
                  {products.map((product) => (
                    <option value={product.id} key={product.id}>
                      {product.product_code} · {product.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="wide">
                标题
                <input required value={form.title} onChange={(event) => change("title", event.target.value)} />
              </label>
              <label>
                链接 handle
                <input required value={form.handle} onChange={(event) => change("handle", event.target.value)} />
              </label>
              <label>
                分类
                <input value={form.category} onChange={(event) => change("category", event.target.value)} />
              </label>
              {form.contentKind === "article" && (
                <label>
                  博客栏目
                  <input value={form.blogHandle} onChange={(event) => change("blogHandle", event.target.value)} />
                </label>
              )}
              {WORKSPACE.localized && !isVideo && (
                <label className="wide">
                  Shopify {WORKSPACE.marketLabel}翻译源资源 GID
                  <input value={form.translationSourceResourceId} onChange={(event) => change("translationSourceResourceId", event.target.value)} placeholder={form.contentKind === "article" ? "gid://shopify/Article/数字" : "gid://shopify/Page/数字"} />
                  <small>仅保存 Shopify 资源引用；发布时注册 {WORKSPACE.marketLabel} Market 的 {WORKSPACE.localeCode} 翻译，不会复制英文资源。</small>
                </label>
              )}
              <label>
                风险状态
                <select value={form.riskStatus} onChange={(event) => change("riskStatus", event.target.value)}>
                  <option value="low">低风险</option>
                  <option value="review">待审核</option>
                  <option value="blocked">阻断</option>
                </select>
              </label>
            </>
          )}
          {section === "copy" && !isVideo && (
            <>
              <label className="wide">
                场景图 URL（最多 3 张，每行一个）
                <textarea value={form.sceneImageUrls} onChange={(event) => change("sceneImageUrls", event.target.value)} />
              </label>
              <label className="wide">
                摘要 HTML
                <textarea value={form.summaryHtml} onChange={(event) => change("summaryHtml", event.target.value)} />
              </label>
              <label className="wide">
                正文 HTML
                <textarea rows={15} value={form.bodyHtml} onChange={(event) => change("bodyHtml", event.target.value)} />
              </label>
            </>
          )}
          {section === "seo" && !isVideo && (
            <>
              <label>
                SEO 标题
                <input value={form.seoTitle} onChange={(event) => change("seoTitle", event.target.value)} />
              </label>
              <label>
                目标关键词
                <input value={form.targetKeyword} onChange={(event) => change("targetKeyword", event.target.value)} />
              </label>
              <label className="wide">
                SEO 描述
                <textarea value={form.seoDescription} onChange={(event) => change("seoDescription", event.target.value)} />
              </label>
              <label>
                作者
                <input value={form.author} onChange={(event) => change("author", event.target.value)} />
              </label>
              <label>
                标签（逗号分隔）
                <input value={form.tags} onChange={(event) => change("tags", event.target.value)} />
              </label>
              <label className="wide">
                证据资料 JSON
                <textarea value={form.evidence} onChange={(event) => change("evidence", event.target.value)} />
              </label>
              <label className="wide">
                风险理由（每行一个）
                <textarea value={form.riskReasons} onChange={(event) => change("riskReasons", event.target.value)} />
              </label>
              <label className="wide">
                内链资料 JSON
                <textarea value={form.internalLinks} onChange={(event) => change("internalLinks", event.target.value)} />
              </label>
            </>
          )}
          {section === "production" && isVideo && (
            <>
              <label className="wide">
                参考视频地址
                <input type="url" value={form.referenceVideoUrl} onChange={(event) => change("referenceVideoUrl", event.target.value)} />
              </label>
              <label className="wide">
                已审核脚本与制作提示
                <textarea required rows={7} value={form.videoPrompt} onChange={(event) => change("videoPrompt", event.target.value)} />
              </label>
              <label className="wide checkbox-line">
                <input type="checkbox" checked={form.approvedScriptOnly} onChange={(event) => change("approvedScriptOnly", event.target.checked)} />
                我确认上方内容已包含可直接制作的完整脚本，并已完成人工审核。
              </label>
              <fieldset className="wide platform-checks">
                <legend>已批准生产素材（至少 1 个）</legend>
                {relevantAssets.length ? (
                  relevantAssets.map((asset) => (
                    <label key={asset.id}>
                      <input type="checkbox" checked={form.assetIds.includes(asset.id)} onChange={() => toggleAsset(asset.id)} />
                      {asset.media_type} · {asset.title}
                    </label>
                  ))
                ) : (
                  <small>该产品暂无已批准、可下载的本地生产素材，请先到素材中心上传并审核。</small>
                )}
              </fieldset>
              <label>
                时长（秒）
                <input inputMode="numeric" value={form.videoDuration} onChange={(event) => change("videoDuration", event.target.value)} />
              </label>
              <label>
                画幅
                <select value={form.aspectRatio} onChange={(event) => change("aspectRatio", event.target.value)}>
                  <option value="9:16">9:16</option>
                  <option value="1:1">1:1</option>
                  <option value="16:9">16:9</option>
                </select>
              </label>
              <label className="wide">
                场景图 URL（最多 3 张，每行一个）
                <textarea value={form.sceneImageUrls} onChange={(event) => change("sceneImageUrls", event.target.value)} />
              </label>
            </>
          )}
          {section === "distribution" && isVideo && (
            <>
              <fieldset className="wide platform-checks">
                <legend>审核通过后分发目标</legend>
                {["facebook", "instagram", "tiktok", "youtube", "pinterest"].map((platform) => (
                  <label key={platform}>
                    <input type="checkbox" checked={form.targetPlatforms.includes(platform)} onChange={() => togglePlatform(platform)} />
                    {platform}
                  </label>
                ))}
              </fieldset>
              <label className="wide">
                成片地址
                <input type="url" value={form.videoOutputUrl} onChange={(event) => change("videoOutputUrl", event.target.value)} />
              </label>
              <label className="wide">
                封面地址
                <input type="url" value={form.coverUrl} onChange={(event) => change("coverUrl", event.target.value)} />
              </label>
              <label className="wide">
                商品落地页
                <input type="url" value={form.landingUrl} onChange={(event) => change("landingUrl", event.target.value)} />
              </label>
              <label className="wide">
                统一社媒正文
                <textarea value={form.caption} onChange={(event) => change("caption", event.target.value)} />
              </label>
              <label className="wide">
                必要披露
                <textarea value={form.disclosure} onChange={(event) => change("disclosure", event.target.value)} />
              </label>
            </>
          )}
        </div>
        {isVideo && <p className="field-note">提交生成只会进入 VIDEO_FACTORY_PROJECT 本地 Codex 队列。更改提示词、参考素材、时长、画幅或场景图会生成新的任务版本。</p>}
        <div className="actions">
          <button className="primary">保存版本</button>
          {!creating && item.status === "draft" && (
            <button className="secondary" type="button" onClick={() => onSubmitReview(item.id)}>
              提交审核
            </button>
          )}
          {!creating && item.status === "approved" && !isVideo && (
            <button className="primary" type="button" onClick={() => onPublish(item.id)}>
              发布到 Shopify
            </button>
          )}
          <button className="secondary" type="button" onClick={onClose}>
            取消
          </button>
        </div>
      </form>
    </div>
  );
}

function ContentPreviewWindow({ item, busy, errorMessage, canApprove, onClose, onEdit, onDecision }: { item: Json; busy: boolean; errorMessage?: string; canApprove: boolean; onClose: () => void; onEdit: () => void; onDecision: (decision: "approve" | "return", note: string) => void }) {
  const body = item.body_json || {};
  const seo = item.seo_json || {};
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");
  const form: Json = {
    title: item.title || "",
    contentKind: item.content_kind || "article",
    summaryHtml: body.summaryHtml || "",
    bodyHtml: body.bodyHtml || "",
    sceneImageUrls: Array.isArray(body.sceneImageUrls) ? body.sceneImageUrls.join("\n") : "",
    videoOutputUrl: body.videoOutputUrl || "",
    coverUrl: body.coverUrl || body.videoCoverUrl || "",
    referenceVideoUrl: body.referenceVideoUrl || "",
    targetPlatforms: Array.isArray(body.targetPlatforms) ? body.targetPlatforms : [],
    caption: body.caption || body.socialCaption || "",
    videoPrompt: body.videoPrompt || "",
    disclosure: editorDisclosure(body.disclosure),
    seoTitle: seo.title || "",
  };
  const imageCount = Array.isArray(body.sceneImageUrls) ? body.sceneImageUrls.filter((url: unknown) => /^https:\/\//i.test(String(url))).length : 0;
  const ready = item.content_kind === "video" ? Boolean(body.videoOutputUrl && (body.coverUrl || body.videoCoverUrl) && body.disclosure) : Boolean(body.bodyHtml && imageCount >= 1 && imageCount <= 3);
  const approve = () => onDecision("approve", "已在内容预览中核对当前版本、图片、风险与发布目标");
  const submitReturn = () => {
    if (note.trim()) onDecision("return", note.trim());
  };

  return (
    <div className="preview-overlay" role="dialog" aria-modal="true">
      <section className="preview-dialog content-preview-window">
        <div className="card-row">
          <div>
            <span className="eyebrow">内容预览</span>
            <h3>{item.title}</h3>
          </div>
          <button className="text-button" onClick={onClose}>
            关闭
          </button>
        </div>
        <ContentPreview form={form} />
        {item.status === "review" && !ready && <p className="error">{item.content_kind === "video" ? "当前视频缺少成片、封面或必要披露，暂不能批准。" : "当前文章必须配置 1–3 张公开 HTTPS 场景图或产品图后才能批准。"}</p>}
        {errorMessage && (
          <p className="error" role="alert">
            审核发布失败：{errorMessage}
          </p>
        )}
        <div className="actions">
          <button className="secondary" onClick={onEdit}>
            编辑
          </button>
          {item.status === "review" && canApprove && (
            <button className="primary" disabled={busy || !ready} onClick={approve}>
              {busy ? "正在审核并发布…" : "审核并发布"}
            </button>
          )}
          {item.status === "review" && canApprove && (
            <button
              className="secondary"
              disabled={busy}
              onClick={() => {
                setReturning(true);
                setNote("");
              }}
            >
              退回修改
            </button>
          )}
          {item.shopify_url && (
            <a className="primary" href={item.shopify_url} target="_blank" rel="noreferrer">
              打开前台
            </a>
          )}
        </div>
        {returning && (
          <div className="inline-review-decision">
            <strong>填写退回原因</strong>
            <textarea required rows={4} value={note} onChange={(event) => setNote(event.target.value)} placeholder="说明需要修改或补充的内容" />
            <div className="actions">
              <button className="danger-button" disabled={busy || !note.trim()} onClick={submitReturn}>
                {busy ? "正在处理…" : "确认退回"}
              </button>
              <button className="secondary" disabled={busy} onClick={() => setReturning(false)}>
                取消
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

async function logout(user: User, setUser: (value: User | null) => void) {
  await api("/auth/logout", {
    method: "POST",
    headers: { "X-CSRF-Token": user.csrfToken },
  }).catch(() => {});
  setUser(null);
}
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
